/**
 * This file was auto-generated.
 * Do not make direct changes to the file.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */

import {
	Mutation as SharedMutation,
	useEnhancedMutation as useSharedEnhancedMutation,
	useEnhancedMutationMaybe as useSharedEnhancedMutationMaybe,
	useEnhancedMutationTrigger as useSharedEnhancedMutationTrigger,
	type DefaultContextType as SharedDefaultContextType
} from '@stack/react-shared'
import { QueryClient, useInfiniteQuery, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { UseMutationOptions, UseMutationResult } from '@tanstack/react-query'
import { createContext, ReactNode, useContext, useMemo } from 'react'
import { Observable, Subscription, filter } from 'rxjs'

import { Api } from './api'
import type {
	EndpointInput,
	EndpointPath,
	EndpointQuery,
	EndpointHeaders,
	EndpointResponse,
	EndpointErrorResponses,
	EndpointNoThrowResult
} from './common'
import { Endpoint } from './common'

export type { ApplyOptimistic, Optimistic, OptimisticChildren } from '@stack/react-shared'

/**
 * Typed call sites for the optimistic-mutation engine in `@stack/react-shared`.
 * The queue, revalidation cache, and mutation protocol live in that package.
 * `createMutation` resolves this project's `QUERY_KEYS`.
 */
export const DEFAULT_PAGE_SIZE = 30

export const createStableQueryKey = (params: any) => {
	return Object.entries(params ?? {})
		.filter(([_, value]) => value !== undefined && value !== null)
		.map(([key, value]) => `${key}:${value}`)
		.join('&')
}

export function waitForObservable<T>(subject: Observable<T>) {
	return new Promise<T>((resolve, reject) => {
		let subscription: Subscription | undefined = undefined
		subscription = subject.subscribe(
			value => {
				resolve(value)
				subscription?.unsubscribe()
			},
			error => reject(error)
		)
	})
}

export function waitUntilTrue(subject: Observable<boolean>) {
	return waitForObservable(subject.pipe(filter(value => value)))
}

export class Mutation<
	TMutation extends Endpoint = Endpoint,
	TMutatee extends Endpoint = Endpoint,
	TOptimistic = EndpointResponse<TMutatee>
> extends SharedMutation<EndpointInput<TMutation>, EndpointResponse<TMutation>, TOptimistic> {
	constructor(
		keyTransformer: (variables: EndpointInput<TMutation>) => { wildcard: true; key: any } | EndpointInput<TMutatee>,
		transformer?: (variables: EndpointInput<TMutation>, input: TOptimistic | undefined) => TOptimistic | undefined,
		responseHandler?: (
			response: EndpointResponse<TMutation>,
			variables: EndpointInput<TMutation>
		) => EndpointResponse<TMutatee> | void
	) {
		type EngineMutation = SharedMutation<EndpointInput<TMutation>, EndpointResponse<TMutation>, TOptimistic>
		super(
			keyTransformer as EngineMutation['keyTransformer'],
			transformer,
			responseHandler as EngineMutation['responseHandler']
		)
	}
}

type DefaultContextType = {
	previousValues: Record<string, any>
	requestId: string
	mutations: Mutation<any, any, any>[]
}

export type MutationConfig<T extends Endpoint> = UseMutationOptions<
	EndpointNoThrowResult<T>,
	Error,
	EndpointInput<T>,
	DefaultContextType
>

function sharedMutations<TMutation extends Endpoint>(mutations: Mutation<TMutation, any, any>[]) {
	return mutations as unknown as SharedMutation<EndpointInput<TMutation>, unknown, unknown>[]
}

function sharedConfig<TMutation extends Endpoint, TData>(
	config: UseMutationOptions<TData, Error, EndpointInput<TMutation>, DefaultContextType> | undefined
) {
	return config as UseMutationOptions<TData, Error, EndpointInput<TMutation>, SharedDefaultContextType> | undefined
}

export const useEnhancedMutation = <TMutation extends Endpoint, TData = EndpointNoThrowResult<TMutation>>(
	mutationFn: (variables: EndpointInput<TMutation>) => Promise<TData>,
	config?: UseMutationOptions<TData, Error, EndpointInput<TMutation>, DefaultContextType>,
	...mutations: Mutation<TMutation, any, any>[]
): UseMutationResult<TData, Error, EndpointInput<TMutation>, DefaultContextType> => {
	return useSharedEnhancedMutation<EndpointInput<TMutation>, TData>(
		mutationFn,
		sharedConfig(config),
		...sharedMutations(mutations)
	) as UseMutationResult<TData, Error, EndpointInput<TMutation>, DefaultContextType>
}

export const useEnhancedMutationTrigger = <TMutation extends Endpoint, TData = EndpointNoThrowResult<TMutation>>(
	mutationFn: (variables: EndpointInput<TMutation>) => Promise<TData>,
	config?: UseMutationOptions<TData, Error, EndpointInput<TMutation>, DefaultContextType>,
	...mutations: Mutation<TMutation, any, any>[]
): EnhancedMutationTriggerFn<TMutation, TData> => {
	return useSharedEnhancedMutationTrigger<EndpointInput<TMutation>, TData>(
		mutationFn,
		sharedConfig(config),
		...sharedMutations(mutations)
	)
}

export type EnhancedMutationTriggerFn<TMutation extends Endpoint, TData = EndpointNoThrowResult<TMutation>> = (
	variables: EndpointInput<TMutation>
) => Promise<TData>

export type EnhancedMutationApi<
	TMutation extends Endpoint,
	Subscribe extends boolean,
	TData = EndpointNoThrowResult<TMutation>
> = Subscribe extends true
	? UseMutationResult<TData, Error, EndpointInput<TMutation>, DefaultContextType>
	: EnhancedMutationTriggerFn<TMutation, TData>

export function useEnhancedMutationMaybe<
	TMutation extends Endpoint,
	Subscribe extends boolean = true,
	TData = EndpointNoThrowResult<TMutation>
>(
	subscribe: Subscribe,
	mutationFn: (variables: EndpointInput<TMutation>) => Promise<TData>,
	config?: UseMutationOptions<TData, Error, EndpointInput<TMutation>, DefaultContextType>,
	...mutations: Mutation<TMutation, any, any>[]
): EnhancedMutationApi<TMutation, Subscribe, TData> {
	return useSharedEnhancedMutationMaybe<EndpointInput<TMutation>, Subscribe, TData>(
		subscribe,
		mutationFn,
		sharedConfig(config),
		...sharedMutations(mutations)
	) as EnhancedMutationApi<TMutation, Subscribe, TData>
}

const isWildcardKey = (key: any): key is { wildcard: true; key: any } => 'wildcard' in key && key.wildcard

export const createMutation = <
	TMutation extends Endpoint,
	TMutatee extends Endpoint = Endpoint,
	TOptimistic = EndpointResponse<TMutatee>
>(
	endpoint: TMutatee,
	keyTransformer: (input: EndpointInput<TMutation>) => { wildcard: true; key: any } | EndpointInput<TMutatee>,
	transformer?: (variables: EndpointInput<TMutation>, input: TOptimistic | undefined) => TOptimistic | undefined,
	responseHandler?: (
		response: EndpointResponse<TMutation>,
		variables: EndpointInput<TMutation>
	) => EndpointResponse<TMutatee> | void
) => {
	return new Mutation<TMutation, TMutatee, TOptimistic>(
		(input: EndpointInput<TMutation>) => {
			const transform = keyTransformer(input)
			// A wildcard input becomes its inner key before the engine hashes it.
			if (isWildcardKey(transform)) {
				return transform.key
			}
			return (QUERY_KEYS as any)[endpoint](transform as EndpointInput<TMutatee>)
		},
		transformer,
		responseHandler
	)
}

export type PreloadContext = {
	api: Api
	queryClient: QueryClient
}

export interface ApiContextValue {
	api: Api
}

const ApiContext = createContext<ApiContextValue>(undefined as any)

export const useApiContext = () => {
	const value = useContext(ApiContext)
	if (!value) {
		throw new Error('useApiContext must be used within a ApiProvider')
	}
	return value
}

export const ApiProvider = ({ api, children }: { api: Api; children: ReactNode }) => {
	const value = useMemo<ApiContextValue>(() => ({ api }), [api])
	return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
}

export const getGetApiKeyOptions = (api: Api, {}: EndpointInput<Endpoint.GetApiKey>) => ({
	queryKey: [Endpoint.GetApiKey],
	queryFn: () => api.getApiKey({})
})

export const preloadGetApiKey = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetApiKey>) => {
	return queryClient.ensureQueryData(getGetApiKeyOptions(api, input))
}

export const useGetApiKey = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetApiKey>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetApiKeyOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseLogin = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.Login>,
	...mutations: Mutation<Endpoint.Login, any, any>[]
): EnhancedMutationApi<Endpoint.Login, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.Login, Subscribe>(
		subscribe,
		input => api.loginNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseRegister = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.Register>,
	...mutations: Mutation<Endpoint.Register, any, any>[]
): EnhancedMutationApi<Endpoint.Register, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.Register, Subscribe>(
		subscribe,
		input => api.registerNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseResetPassword = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ResetPassword>,
	...mutations: Mutation<Endpoint.ResetPassword, any, any>[]
): EnhancedMutationApi<Endpoint.ResetPassword, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ResetPassword, Subscribe>(
		subscribe,
		input => api.resetPasswordNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdatePassword = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdatePassword>,
	...mutations: Mutation<Endpoint.UpdatePassword, any, any>[]
): EnhancedMutationApi<Endpoint.UpdatePassword, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdatePassword, Subscribe>(
		subscribe,
		input => api.updatePasswordNoThrow(input),
		config,
		...mutations
	)
}

export const getListUsersOptions = (api: Api, {}: EndpointInput<Endpoint.ListUsers>) => ({
	queryKey: [Endpoint.ListUsers],
	queryFn: () => api.listUsers({})
})

export const preloadListUsers = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.ListUsers>) => {
	return queryClient.ensureQueryData(getListUsersOptions(api, input))
}

export const useListUsers = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListUsers>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getListUsersOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetMeOptions = (api: Api, {}: EndpointInput<Endpoint.GetMe>) => ({
	queryKey: [Endpoint.GetMe],
	queryFn: () => api.getMe({})
})

export const preloadGetMe = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetMe>) => {
	return queryClient.ensureQueryData(getGetMeOptions(api, input))
}

export const useGetMe = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetMe>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetMeOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseUpdateMe = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateMe>,
	...mutations: Mutation<Endpoint.UpdateMe, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateMe, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateMe, Subscribe>(
		subscribe,
		input => api.updateMeNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseValidateMySignInPin = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ValidateMySignInPin>,
	...mutations: Mutation<Endpoint.ValidateMySignInPin, any, any>[]
): EnhancedMutationApi<Endpoint.ValidateMySignInPin, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ValidateMySignInPin, Subscribe>(
		subscribe,
		input => api.validateMySignInPinNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseSetMySignInPin = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetMySignInPin>,
	...mutations: Mutation<Endpoint.SetMySignInPin, any, any>[]
): EnhancedMutationApi<Endpoint.SetMySignInPin, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SetMySignInPin, Subscribe>(
		subscribe,
		input => api.setMySignInPinNoThrow(input),
		config,
		...mutations
	)
}

export const getGetUserOptions = (api: Api, { path }: EndpointInput<Endpoint.GetUser>) => ({
	queryKey: [Endpoint.GetUser, createStableQueryKey(path)],
	queryFn: () => api.getUser({ path })
})

export const preloadGetUser = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetUser>) => {
	return queryClient.ensureQueryData(getGetUserOptions(api, input))
}

export const useGetUser = (
	path: EndpointPath<Endpoint.GetUser>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetUser>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetUserOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.userUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateUser = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateUser>,
	...mutations: Mutation<Endpoint.UpdateUser, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateUser, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateUser, Subscribe>(
		subscribe,
		input => api.updateUserNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteUser = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteUser>,
	...mutations: Mutation<Endpoint.DeleteUser, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteUser, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteUser, Subscribe>(
		subscribe,
		input => api.deleteUserNoThrow(input),
		config,
		...mutations
	)
}

export const getGetUserSignInCredentialsOptions = (
	api: Api,
	{ path }: EndpointInput<Endpoint.GetUserSignInCredentials>
) => ({
	queryKey: [Endpoint.GetUserSignInCredentials, createStableQueryKey(path)],
	queryFn: () => api.getUserSignInCredentials({ path })
})

export const preloadGetUserSignInCredentials = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetUserSignInCredentials>
) => {
	return queryClient.ensureQueryData(getGetUserSignInCredentialsOptions(api, input))
}

export const useGetUserSignInCredentials = (
	path: EndpointPath<Endpoint.GetUserSignInCredentials>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetUserSignInCredentials>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetUserSignInCredentialsOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.userUuid && (config?.enabled ?? true)
	})
}

export const useBaseSetUserSignInCredentials = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetUserSignInCredentials>,
	...mutations: Mutation<Endpoint.SetUserSignInCredentials, any, any>[]
): EnhancedMutationApi<Endpoint.SetUserSignInCredentials, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SetUserSignInCredentials, Subscribe>(
		subscribe,
		input => api.setUserSignInCredentialsNoThrow(input),
		config,
		...mutations
	)
}

export const getListInvitesOptions = (api: Api, {}: EndpointInput<Endpoint.ListInvites>) => ({
	queryKey: [Endpoint.ListInvites],
	queryFn: () => api.listInvites({})
})

export const preloadListInvites = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListInvites>
) => {
	return queryClient.ensureQueryData(getListInvitesOptions(api, input))
}

export const useListInvites = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListInvites>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getListInvitesOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreateInvite = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateInvite>,
	...mutations: Mutation<Endpoint.CreateInvite, any, any>[]
): EnhancedMutationApi<Endpoint.CreateInvite, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateInvite, Subscribe>(
		subscribe,
		input => api.createInviteNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteInvite = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteInvite>,
	...mutations: Mutation<Endpoint.DeleteInvite, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteInvite, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteInvite, Subscribe>(
		subscribe,
		input => api.deleteInviteNoThrow(input),
		config,
		...mutations
	)
}

export const getListSalesChannelTypesOptions = (api: Api, {}: EndpointInput<Endpoint.ListSalesChannelTypes>) => ({
	queryKey: [Endpoint.ListSalesChannelTypes],
	queryFn: () => api.listSalesChannelTypes({})
})

export const preloadListSalesChannelTypes = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListSalesChannelTypes>
) => {
	return queryClient.ensureQueryData(getListSalesChannelTypesOptions(api, input))
}

export const useListSalesChannelTypes = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListSalesChannelTypes>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListSalesChannelTypesOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreateSalesChannelType = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSalesChannelType>,
	...mutations: Mutation<Endpoint.CreateSalesChannelType, any, any>[]
): EnhancedMutationApi<Endpoint.CreateSalesChannelType, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateSalesChannelType, Subscribe>(
		subscribe,
		input => api.createSalesChannelTypeNoThrow(input),
		config,
		...mutations
	)
}

export const getGetSalesChannelTypeOptions = (api: Api, { path }: EndpointInput<Endpoint.GetSalesChannelType>) => ({
	queryKey: [Endpoint.GetSalesChannelType, createStableQueryKey(path)],
	queryFn: () => api.getSalesChannelType({ path })
})

export const preloadGetSalesChannelType = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetSalesChannelType>
) => {
	return queryClient.ensureQueryData(getGetSalesChannelTypeOptions(api, input))
}

export const useGetSalesChannelType = (
	path: EndpointPath<Endpoint.GetSalesChannelType>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetSalesChannelType>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetSalesChannelTypeOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.salesChannelTypeUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateSalesChannelType = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateSalesChannelType>,
	...mutations: Mutation<Endpoint.UpdateSalesChannelType, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateSalesChannelType, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateSalesChannelType, Subscribe>(
		subscribe,
		input => api.updateSalesChannelTypeNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteSalesChannelType = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteSalesChannelType>,
	...mutations: Mutation<Endpoint.DeleteSalesChannelType, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteSalesChannelType, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteSalesChannelType, Subscribe>(
		subscribe,
		input => api.deleteSalesChannelTypeNoThrow(input),
		config,
		...mutations
	)
}

export const getListSalesChannelGroupsOptions = (api: Api, {}: EndpointInput<Endpoint.ListSalesChannelGroups>) => ({
	queryKey: [Endpoint.ListSalesChannelGroups],
	queryFn: () => api.listSalesChannelGroups({})
})

export const preloadListSalesChannelGroups = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListSalesChannelGroups>
) => {
	return queryClient.ensureQueryData(getListSalesChannelGroupsOptions(api, input))
}

export const useListSalesChannelGroups = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListSalesChannelGroups>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListSalesChannelGroupsOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreateSalesChannelGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSalesChannelGroup>,
	...mutations: Mutation<Endpoint.CreateSalesChannelGroup, any, any>[]
): EnhancedMutationApi<Endpoint.CreateSalesChannelGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateSalesChannelGroup, Subscribe>(
		subscribe,
		input => api.createSalesChannelGroupNoThrow(input),
		config,
		...mutations
	)
}

export const getGetSalesChannelGroupOptions = (api: Api, { path }: EndpointInput<Endpoint.GetSalesChannelGroup>) => ({
	queryKey: [Endpoint.GetSalesChannelGroup, createStableQueryKey(path)],
	queryFn: () => api.getSalesChannelGroup({ path })
})

export const preloadGetSalesChannelGroup = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetSalesChannelGroup>
) => {
	return queryClient.ensureQueryData(getGetSalesChannelGroupOptions(api, input))
}

export const useGetSalesChannelGroup = (
	path: EndpointPath<Endpoint.GetSalesChannelGroup>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetSalesChannelGroup>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetSalesChannelGroupOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.salesChannelGroupUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateSalesChannelGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateSalesChannelGroup>,
	...mutations: Mutation<Endpoint.UpdateSalesChannelGroup, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateSalesChannelGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateSalesChannelGroup, Subscribe>(
		subscribe,
		input => api.updateSalesChannelGroupNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteSalesChannelGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteSalesChannelGroup>,
	...mutations: Mutation<Endpoint.DeleteSalesChannelGroup, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteSalesChannelGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteSalesChannelGroup, Subscribe>(
		subscribe,
		input => api.deleteSalesChannelGroupNoThrow(input),
		config,
		...mutations
	)
}

export const getListSalesChannelsOptions = (api: Api, {}: EndpointInput<Endpoint.ListSalesChannels>) => ({
	queryKey: [Endpoint.ListSalesChannels],
	queryFn: () => api.listSalesChannels({})
})

export const preloadListSalesChannels = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListSalesChannels>
) => {
	return queryClient.ensureQueryData(getListSalesChannelsOptions(api, input))
}

export const useListSalesChannels = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListSalesChannels>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListSalesChannelsOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreateSalesChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSalesChannel>,
	...mutations: Mutation<Endpoint.CreateSalesChannel, any, any>[]
): EnhancedMutationApi<Endpoint.CreateSalesChannel, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateSalesChannel, Subscribe>(
		subscribe,
		input => api.createSalesChannelNoThrow(input),
		config,
		...mutations
	)
}

export const getGetSalesChannelOptions = (api: Api, { path }: EndpointInput<Endpoint.GetSalesChannel>) => ({
	queryKey: [Endpoint.GetSalesChannel, createStableQueryKey(path)],
	queryFn: () => api.getSalesChannel({ path })
})

export const preloadGetSalesChannel = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetSalesChannel>
) => {
	return queryClient.ensureQueryData(getGetSalesChannelOptions(api, input))
}

export const useGetSalesChannel = (
	path: EndpointPath<Endpoint.GetSalesChannel>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetSalesChannel>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetSalesChannelOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.salesChannelUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateSalesChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateSalesChannel>,
	...mutations: Mutation<Endpoint.UpdateSalesChannel, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateSalesChannel, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateSalesChannel, Subscribe>(
		subscribe,
		input => api.updateSalesChannelNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteSalesChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteSalesChannel>,
	...mutations: Mutation<Endpoint.DeleteSalesChannel, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteSalesChannel, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteSalesChannel, Subscribe>(
		subscribe,
		input => api.deleteSalesChannelNoThrow(input),
		config,
		...mutations
	)
}

export const getListPointOfSaleGroupsOptions = (api: Api, {}: EndpointInput<Endpoint.ListPointOfSaleGroups>) => ({
	queryKey: [Endpoint.ListPointOfSaleGroups],
	queryFn: () => api.listPointOfSaleGroups({})
})

export const preloadListPointOfSaleGroups = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListPointOfSaleGroups>
) => {
	return queryClient.ensureQueryData(getListPointOfSaleGroupsOptions(api, input))
}

export const useListPointOfSaleGroups = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListPointOfSaleGroups>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListPointOfSaleGroupsOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreatePointOfSaleGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePointOfSaleGroup>,
	...mutations: Mutation<Endpoint.CreatePointOfSaleGroup, any, any>[]
): EnhancedMutationApi<Endpoint.CreatePointOfSaleGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreatePointOfSaleGroup, Subscribe>(
		subscribe,
		input => api.createPointOfSaleGroupNoThrow(input),
		config,
		...mutations
	)
}

export const getGetPointOfSaleGroupOptions = (api: Api, { path }: EndpointInput<Endpoint.GetPointOfSaleGroup>) => ({
	queryKey: [Endpoint.GetPointOfSaleGroup, createStableQueryKey(path)],
	queryFn: () => api.getPointOfSaleGroup({ path })
})

export const preloadGetPointOfSaleGroup = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetPointOfSaleGroup>
) => {
	return queryClient.ensureQueryData(getGetPointOfSaleGroupOptions(api, input))
}

export const useGetPointOfSaleGroup = (
	path: EndpointPath<Endpoint.GetPointOfSaleGroup>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetPointOfSaleGroup>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetPointOfSaleGroupOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.pointOfSaleGroupUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdatePointOfSaleGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdatePointOfSaleGroup>,
	...mutations: Mutation<Endpoint.UpdatePointOfSaleGroup, any, any>[]
): EnhancedMutationApi<Endpoint.UpdatePointOfSaleGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdatePointOfSaleGroup, Subscribe>(
		subscribe,
		input => api.updatePointOfSaleGroupNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeletePointOfSaleGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeletePointOfSaleGroup>,
	...mutations: Mutation<Endpoint.DeletePointOfSaleGroup, any, any>[]
): EnhancedMutationApi<Endpoint.DeletePointOfSaleGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeletePointOfSaleGroup, Subscribe>(
		subscribe,
		input => api.deletePointOfSaleGroupNoThrow(input),
		config,
		...mutations
	)
}

export const getListPointsOfSaleOptions = (api: Api, {}: EndpointInput<Endpoint.ListPointsOfSale>) => ({
	queryKey: [Endpoint.ListPointsOfSale],
	queryFn: () => api.listPointsOfSale({})
})

export const preloadListPointsOfSale = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListPointsOfSale>
) => {
	return queryClient.ensureQueryData(getListPointsOfSaleOptions(api, input))
}

export const useListPointsOfSale = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListPointsOfSale>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getListPointsOfSaleOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreatePointOfSale = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePointOfSale>,
	...mutations: Mutation<Endpoint.CreatePointOfSale, any, any>[]
): EnhancedMutationApi<Endpoint.CreatePointOfSale, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreatePointOfSale, Subscribe>(
		subscribe,
		input => api.createPointOfSaleNoThrow(input),
		config,
		...mutations
	)
}

export const getGetPointOfSaleHierarchyOptions = (api: Api, {}: EndpointInput<Endpoint.GetPointOfSaleHierarchy>) => ({
	queryKey: [Endpoint.GetPointOfSaleHierarchy],
	queryFn: () => api.getPointOfSaleHierarchy({})
})

export const preloadGetPointOfSaleHierarchy = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetPointOfSaleHierarchy>
) => {
	return queryClient.ensureQueryData(getGetPointOfSaleHierarchyOptions(api, input))
}

export const useGetPointOfSaleHierarchy = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetPointOfSaleHierarchy>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetPointOfSaleHierarchyOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetCurrentPointOfSaleOptions = (api: Api, {}: EndpointInput<Endpoint.GetCurrentPointOfSale>) => ({
	queryKey: [Endpoint.GetCurrentPointOfSale],
	queryFn: () => api.getCurrentPointOfSale({})
})

export const preloadGetCurrentPointOfSale = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetCurrentPointOfSale>
) => {
	return queryClient.ensureQueryData(getGetCurrentPointOfSaleOptions(api, input))
}

export const useGetCurrentPointOfSale = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetCurrentPointOfSale>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetCurrentPointOfSaleOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreatePointOfSaleWebSocketTicket = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePointOfSaleWebSocketTicket>,
	...mutations: Mutation<Endpoint.CreatePointOfSaleWebSocketTicket, any, any>[]
): EnhancedMutationApi<Endpoint.CreatePointOfSaleWebSocketTicket, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreatePointOfSaleWebSocketTicket, Subscribe>(
		subscribe,
		input => api.createPointOfSaleWebSocketTicketNoThrow(input),
		config,
		...mutations
	)
}

export const getGetPointOfSaleOptions = (api: Api, { path }: EndpointInput<Endpoint.GetPointOfSale>) => ({
	queryKey: [Endpoint.GetPointOfSale, createStableQueryKey(path)],
	queryFn: () => api.getPointOfSale({ path })
})

export const preloadGetPointOfSale = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetPointOfSale>
) => {
	return queryClient.ensureQueryData(getGetPointOfSaleOptions(api, input))
}

export const useGetPointOfSale = (
	path: EndpointPath<Endpoint.GetPointOfSale>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetPointOfSale>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetPointOfSaleOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.pointOfSaleUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdatePointOfSale = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdatePointOfSale>,
	...mutations: Mutation<Endpoint.UpdatePointOfSale, any, any>[]
): EnhancedMutationApi<Endpoint.UpdatePointOfSale, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdatePointOfSale, Subscribe>(
		subscribe,
		input => api.updatePointOfSaleNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseOpenCooler = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.OpenCooler>,
	...mutations: Mutation<Endpoint.OpenCooler, any, any>[]
): EnhancedMutationApi<Endpoint.OpenCooler, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.OpenCooler, Subscribe>(
		subscribe,
		input => api.openCoolerNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseOpenCashFloat = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.OpenCashFloat>,
	...mutations: Mutation<Endpoint.OpenCashFloat, any, any>[]
): EnhancedMutationApi<Endpoint.OpenCashFloat, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.OpenCashFloat, Subscribe>(
		subscribe,
		input => api.openCashFloatNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCashPayIn = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CashPayIn>,
	...mutations: Mutation<Endpoint.CashPayIn, any, any>[]
): EnhancedMutationApi<Endpoint.CashPayIn, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CashPayIn, Subscribe>(
		subscribe,
		input => api.cashPayInNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCashPayOut = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CashPayOut>,
	...mutations: Mutation<Endpoint.CashPayOut, any, any>[]
): EnhancedMutationApi<Endpoint.CashPayOut, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CashPayOut, Subscribe>(
		subscribe,
		input => api.cashPayOutNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCashCount = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CashCount>,
	...mutations: Mutation<Endpoint.CashCount, any, any>[]
): EnhancedMutationApi<Endpoint.CashCount, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CashCount, Subscribe>(
		subscribe,
		input => api.cashCountNoThrow(input),
		config,
		...mutations
	)
}

export const getGetPointOfSaleDeviceStatusOptions = (
	api: Api,
	{ path }: EndpointInput<Endpoint.GetPointOfSaleDeviceStatus>
) => ({
	queryKey: [Endpoint.GetPointOfSaleDeviceStatus, createStableQueryKey(path)],
	queryFn: () => api.getPointOfSaleDeviceStatus({ path })
})

export const preloadGetPointOfSaleDeviceStatus = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetPointOfSaleDeviceStatus>
) => {
	return queryClient.ensureQueryData(getGetPointOfSaleDeviceStatusOptions(api, input))
}

export const useGetPointOfSaleDeviceStatus = (
	path: EndpointPath<Endpoint.GetPointOfSaleDeviceStatus>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetPointOfSaleDeviceStatus>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetPointOfSaleDeviceStatusOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.pointOfSaleUuid && (config?.enabled ?? true)
	})
}

export const useBaseRefreshPointOfSaleDeviceStatus = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RefreshPointOfSaleDeviceStatus>,
	...mutations: Mutation<Endpoint.RefreshPointOfSaleDeviceStatus, any, any>[]
): EnhancedMutationApi<Endpoint.RefreshPointOfSaleDeviceStatus, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RefreshPointOfSaleDeviceStatus, Subscribe>(
		subscribe,
		input => api.refreshPointOfSaleDeviceStatusNoThrow(input),
		config,
		...mutations
	)
}

export const getLookupDeviceEnrollmentByCodeOptions = (
	api: Api,
	{ path }: EndpointInput<Endpoint.LookupDeviceEnrollmentByCode>
) => ({
	queryKey: [Endpoint.LookupDeviceEnrollmentByCode, createStableQueryKey(path)],
	queryFn: () => api.lookupDeviceEnrollmentByCode({ path })
})

export const preloadLookupDeviceEnrollmentByCode = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.LookupDeviceEnrollmentByCode>
) => {
	return queryClient.ensureQueryData(getLookupDeviceEnrollmentByCodeOptions(api, input))
}

export const useLookupDeviceEnrollmentByCode = (
	path: EndpointPath<Endpoint.LookupDeviceEnrollmentByCode>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.LookupDeviceEnrollmentByCode>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getLookupDeviceEnrollmentByCodeOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.pointOfSaleUuid && !!path.code && (config?.enabled ?? true)
	})
}

export const useBaseEnrollPointOfSaleDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.EnrollPointOfSaleDevice>,
	...mutations: Mutation<Endpoint.EnrollPointOfSaleDevice, any, any>[]
): EnhancedMutationApi<Endpoint.EnrollPointOfSaleDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.EnrollPointOfSaleDevice, Subscribe>(
		subscribe,
		input => api.enrollPointOfSaleDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUnenrollPointOfSaleDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UnenrollPointOfSaleDevice>,
	...mutations: Mutation<Endpoint.UnenrollPointOfSaleDevice, any, any>[]
): EnhancedMutationApi<Endpoint.UnenrollPointOfSaleDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UnenrollPointOfSaleDevice, Subscribe>(
		subscribe,
		input => api.unenrollPointOfSaleDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseNotifyPointOfSaleAppUpdate = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.NotifyPointOfSaleAppUpdate>,
	...mutations: Mutation<Endpoint.NotifyPointOfSaleAppUpdate, any, any>[]
): EnhancedMutationApi<Endpoint.NotifyPointOfSaleAppUpdate, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.NotifyPointOfSaleAppUpdate, Subscribe>(
		subscribe,
		input => api.notifyPointOfSaleAppUpdateNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseRestartPointOfSaleApp = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RestartPointOfSaleApp>,
	...mutations: Mutation<Endpoint.RestartPointOfSaleApp, any, any>[]
): EnhancedMutationApi<Endpoint.RestartPointOfSaleApp, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RestartPointOfSaleApp, Subscribe>(
		subscribe,
		input => api.restartPointOfSaleAppNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseLockPointOfSaleKiosk = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.LockPointOfSaleKiosk>,
	...mutations: Mutation<Endpoint.LockPointOfSaleKiosk, any, any>[]
): EnhancedMutationApi<Endpoint.LockPointOfSaleKiosk, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.LockPointOfSaleKiosk, Subscribe>(
		subscribe,
		input => api.lockPointOfSaleKioskNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUnlockPointOfSaleKiosk = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UnlockPointOfSaleKiosk>,
	...mutations: Mutation<Endpoint.UnlockPointOfSaleKiosk, any, any>[]
): EnhancedMutationApi<Endpoint.UnlockPointOfSaleKiosk, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UnlockPointOfSaleKiosk, Subscribe>(
		subscribe,
		input => api.unlockPointOfSaleKioskNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUnlockPointOfSaleDoor = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UnlockPointOfSaleDoor>,
	...mutations: Mutation<Endpoint.UnlockPointOfSaleDoor, any, any>[]
): EnhancedMutationApi<Endpoint.UnlockPointOfSaleDoor, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UnlockPointOfSaleDoor, Subscribe>(
		subscribe,
		input => api.unlockPointOfSaleDoorNoThrow(input),
		config,
		...mutations
	)
}

export const getGetGmsPlayTracksOptions = (api: Api, {}: EndpointInput<Endpoint.GetGmsPlayTracks>) => ({
	queryKey: [Endpoint.GetGmsPlayTracks],
	queryFn: () => api.getGmsPlayTracks({})
})

export const preloadGetGmsPlayTracks = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetGmsPlayTracks>
) => {
	return queryClient.ensureQueryData(getGetGmsPlayTracksOptions(api, input))
}

export const useGetGmsPlayTracks = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetGmsPlayTracks>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetGmsPlayTracksOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getListPublishableKeysOptions = (api: Api, { query }: EndpointInput<Endpoint.ListPublishableKeys>) => ({
	queryKey: [Endpoint.ListPublishableKeys, createStableQueryKey(query)],
	queryFn: () => api.listPublishableKeys({ query })
})

export const preloadListPublishableKeys = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListPublishableKeys>
) => {
	return queryClient.ensureQueryData(getListPublishableKeysOptions(api, input))
}

export const useListPublishableKeys = (
	query: EndpointQuery<Endpoint.ListPublishableKeys>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListPublishableKeys>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListPublishableKeysOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreatePublishableKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePublishableKey>,
	...mutations: Mutation<Endpoint.CreatePublishableKey, any, any>[]
): EnhancedMutationApi<Endpoint.CreatePublishableKey, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreatePublishableKey, Subscribe>(
		subscribe,
		input => api.createPublishableKeyNoThrow(input),
		config,
		...mutations
	)
}

export const getGetPublishableKeyOptions = (api: Api, { path }: EndpointInput<Endpoint.GetPublishableKey>) => ({
	queryKey: [Endpoint.GetPublishableKey, createStableQueryKey(path)],
	queryFn: () => api.getPublishableKey({ path })
})

export const preloadGetPublishableKey = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetPublishableKey>
) => {
	return queryClient.ensureQueryData(getGetPublishableKeyOptions(api, input))
}

export const useGetPublishableKey = (
	path: EndpointPath<Endpoint.GetPublishableKey>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetPublishableKey>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetPublishableKeyOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.publishableKeyUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdatePublishableKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdatePublishableKey>,
	...mutations: Mutation<Endpoint.UpdatePublishableKey, any, any>[]
): EnhancedMutationApi<Endpoint.UpdatePublishableKey, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdatePublishableKey, Subscribe>(
		subscribe,
		input => api.updatePublishableKeyNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeletePublishableKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeletePublishableKey>,
	...mutations: Mutation<Endpoint.DeletePublishableKey, any, any>[]
): EnhancedMutationApi<Endpoint.DeletePublishableKey, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeletePublishableKey, Subscribe>(
		subscribe,
		input => api.deletePublishableKeyNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCreateSecretKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSecretKey>,
	...mutations: Mutation<Endpoint.CreateSecretKey, any, any>[]
): EnhancedMutationApi<Endpoint.CreateSecretKey, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateSecretKey, Subscribe>(
		subscribe,
		input => api.createSecretKeyNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateSecretKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateSecretKey>,
	...mutations: Mutation<Endpoint.UpdateSecretKey, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateSecretKey, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateSecretKey, Subscribe>(
		subscribe,
		input => api.updateSecretKeyNoThrow(input),
		config,
		...mutations
	)
}

export const getListDevicesOptions = (api: Api, {}: EndpointInput<Endpoint.ListDevices>) => ({
	queryKey: [Endpoint.ListDevices],
	queryFn: () => api.listDevices({})
})

export const preloadListDevices = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListDevices>
) => {
	return queryClient.ensureQueryData(getListDevicesOptions(api, input))
}

export const useListDevices = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListDevices>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getListDevicesOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreateDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateDevice>,
	...mutations: Mutation<Endpoint.CreateDevice, any, any>[]
): EnhancedMutationApi<Endpoint.CreateDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateDevice, Subscribe>(
		subscribe,
		input => api.createDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const getGetCurrentDeviceOptions = (api: Api, {}: EndpointInput<Endpoint.GetCurrentDevice>) => ({
	queryKey: [Endpoint.GetCurrentDevice],
	queryFn: () => api.getCurrentDevice({})
})

export const preloadGetCurrentDevice = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetCurrentDevice>
) => {
	return queryClient.ensureQueryData(getGetCurrentDeviceOptions(api, input))
}

export const useGetCurrentDevice = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetCurrentDevice>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetCurrentDeviceOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseReportDeviceStatus = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ReportDeviceStatus>,
	...mutations: Mutation<Endpoint.ReportDeviceStatus, any, any>[]
): EnhancedMutationApi<Endpoint.ReportDeviceStatus, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ReportDeviceStatus, Subscribe>(
		subscribe,
		input => api.reportDeviceStatusNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUnenrollCurrentDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UnenrollCurrentDevice>,
	...mutations: Mutation<Endpoint.UnenrollCurrentDevice, any, any>[]
): EnhancedMutationApi<Endpoint.UnenrollCurrentDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UnenrollCurrentDevice, Subscribe>(
		subscribe,
		input => api.unenrollCurrentDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const getGetDeviceOptions = (api: Api, { path }: EndpointInput<Endpoint.GetDevice>) => ({
	queryKey: [Endpoint.GetDevice, createStableQueryKey(path)],
	queryFn: () => api.getDevice({ path })
})

export const preloadGetDevice = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetDevice>) => {
	return queryClient.ensureQueryData(getGetDeviceOptions(api, input))
}

export const useGetDevice = (
	path: EndpointPath<Endpoint.GetDevice>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetDevice>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetDeviceOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.deviceUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateDevice>,
	...mutations: Mutation<Endpoint.UpdateDevice, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateDevice, Subscribe>(
		subscribe,
		input => api.updateDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteDevice>,
	...mutations: Mutation<Endpoint.DeleteDevice, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteDevice, Subscribe>(
		subscribe,
		input => api.deleteDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseLinkDeviceToPointOfSale = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.LinkDeviceToPointOfSale>,
	...mutations: Mutation<Endpoint.LinkDeviceToPointOfSale, any, any>[]
): EnhancedMutationApi<Endpoint.LinkDeviceToPointOfSale, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.LinkDeviceToPointOfSale, Subscribe>(
		subscribe,
		input => api.linkDeviceToPointOfSaleNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUnlinkDeviceFromPointOfSale = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UnlinkDeviceFromPointOfSale>,
	...mutations: Mutation<Endpoint.UnlinkDeviceFromPointOfSale, any, any>[]
): EnhancedMutationApi<Endpoint.UnlinkDeviceFromPointOfSale, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UnlinkDeviceFromPointOfSale, Subscribe>(
		subscribe,
		input => api.unlinkDeviceFromPointOfSaleNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCreateDeviceSecretKey = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateDeviceSecretKey>,
	...mutations: Mutation<Endpoint.CreateDeviceSecretKey, any, any>[]
): EnhancedMutationApi<Endpoint.CreateDeviceSecretKey, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateDeviceSecretKey, Subscribe>(
		subscribe,
		input => api.createDeviceSecretKeyNoThrow(input),
		config,
		...mutations
	)
}

export const getGetDeviceStatusOptions = (api: Api, { path }: EndpointInput<Endpoint.GetDeviceStatus>) => ({
	queryKey: [Endpoint.GetDeviceStatus, createStableQueryKey(path)],
	queryFn: () => api.getDeviceStatus({ path })
})

export const preloadGetDeviceStatus = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetDeviceStatus>
) => {
	return queryClient.ensureQueryData(getGetDeviceStatusOptions(api, input))
}

export const useGetDeviceStatus = (
	path: EndpointPath<Endpoint.GetDeviceStatus>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetDeviceStatus>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetDeviceStatusOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.deviceUuid && (config?.enabled ?? true)
	})
}

export const useBaseRefreshDeviceStatus = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RefreshDeviceStatus>,
	...mutations: Mutation<Endpoint.RefreshDeviceStatus, any, any>[]
): EnhancedMutationApi<Endpoint.RefreshDeviceStatus, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RefreshDeviceStatus, Subscribe>(
		subscribe,
		input => api.refreshDeviceStatusNoThrow(input),
		config,
		...mutations
	)
}

export const getLookupDeviceEnrollmentByCodeForDeviceOptions = (
	api: Api,
	{ path }: EndpointInput<Endpoint.LookupDeviceEnrollmentByCodeForDevice>
) => ({
	queryKey: [Endpoint.LookupDeviceEnrollmentByCodeForDevice, createStableQueryKey(path)],
	queryFn: () => api.lookupDeviceEnrollmentByCodeForDevice({ path })
})

export const preloadLookupDeviceEnrollmentByCodeForDevice = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.LookupDeviceEnrollmentByCodeForDevice>
) => {
	return queryClient.ensureQueryData(getLookupDeviceEnrollmentByCodeForDeviceOptions(api, input))
}

export const useLookupDeviceEnrollmentByCodeForDevice = (
	path: EndpointPath<Endpoint.LookupDeviceEnrollmentByCodeForDevice>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.LookupDeviceEnrollmentByCodeForDevice>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getLookupDeviceEnrollmentByCodeForDeviceOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.deviceUuid && !!path.code && (config?.enabled ?? true)
	})
}

export const useBaseEnrollDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.EnrollDevice>,
	...mutations: Mutation<Endpoint.EnrollDevice, any, any>[]
): EnhancedMutationApi<Endpoint.EnrollDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.EnrollDevice, Subscribe>(
		subscribe,
		input => api.enrollDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUnenrollDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UnenrollDevice>,
	...mutations: Mutation<Endpoint.UnenrollDevice, any, any>[]
): EnhancedMutationApi<Endpoint.UnenrollDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UnenrollDevice, Subscribe>(
		subscribe,
		input => api.unenrollDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteDeviceGmsDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteDeviceGmsDevice>,
	...mutations: Mutation<Endpoint.DeleteDeviceGmsDevice, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteDeviceGmsDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteDeviceGmsDevice, Subscribe>(
		subscribe,
		input => api.deleteDeviceGmsDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCreateDeviceGmsEnrollment = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateDeviceGmsEnrollment>,
	...mutations: Mutation<Endpoint.CreateDeviceGmsEnrollment, any, any>[]
): EnhancedMutationApi<Endpoint.CreateDeviceGmsEnrollment, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateDeviceGmsEnrollment, Subscribe>(
		subscribe,
		input => api.createDeviceGmsEnrollmentNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseSyncDeviceGmsPolicy = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SyncDeviceGmsPolicy>,
	...mutations: Mutation<Endpoint.SyncDeviceGmsPolicy, any, any>[]
): EnhancedMutationApi<Endpoint.SyncDeviceGmsPolicy, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SyncDeviceGmsPolicy, Subscribe>(
		subscribe,
		input => api.syncDeviceGmsPolicyNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseRebootDeviceGmsDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RebootDeviceGmsDevice>,
	...mutations: Mutation<Endpoint.RebootDeviceGmsDevice, any, any>[]
): EnhancedMutationApi<Endpoint.RebootDeviceGmsDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RebootDeviceGmsDevice, Subscribe>(
		subscribe,
		input => api.rebootDeviceGmsDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const getListProductsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListProducts>) => ({
	queryKey: [Endpoint.ListProducts, createStableQueryKey(query)],
	queryFn: () => api.listProducts({ query })
})

export const preloadListProducts = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListProducts>
) => {
	return queryClient.ensureQueryData(getListProductsOptions(api, input))
}

export const useListProducts = (
	query: EndpointQuery<Endpoint.ListProducts>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListProducts>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListProductsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListProducts = (query: EndpointQuery<Endpoint.ListProducts>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListProducts, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listProducts({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateProduct = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProduct>,
	...mutations: Mutation<Endpoint.CreateProduct, any, any>[]
): EnhancedMutationApi<Endpoint.CreateProduct, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateProduct, Subscribe>(
		subscribe,
		input => api.createProductNoThrow(input),
		config,
		...mutations
	)
}

export const getListProductGroupsForPointOfSaleOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.ListProductGroupsForPointOfSale>
) => ({
	queryKey: [Endpoint.ListProductGroupsForPointOfSale, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.listProductGroupsForPointOfSale({ path, query })
})

export const preloadListProductGroupsForPointOfSale = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListProductGroupsForPointOfSale>
) => {
	return queryClient.ensureQueryData(getListProductGroupsForPointOfSaleOptions(api, input))
}

export const useListProductGroupsForPointOfSale = (
	path: EndpointPath<Endpoint.ListProductGroupsForPointOfSale>,
	query: EndpointQuery<Endpoint.ListProductGroupsForPointOfSale>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListProductGroupsForPointOfSale>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListProductGroupsForPointOfSaleOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.pointOfSaleUuid && (config?.enabled ?? true)
	})
}

export const usePaginatedListProductGroupsForPointOfSale = (
	path: EndpointPath<Endpoint.ListProductGroupsForPointOfSale>,
	query: EndpointQuery<Endpoint.ListProductGroupsForPointOfSale>
) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListProductGroupsForPointOfSale, createStableQueryKey(path), createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) =>
			api.listProductGroupsForPointOfSale({ path, query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseUpdateProductGroupsForPointOfSale = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductGroupsForPointOfSale>,
	...mutations: Mutation<Endpoint.UpdateProductGroupsForPointOfSale, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateProductGroupsForPointOfSale, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateProductGroupsForPointOfSale, Subscribe>(
		subscribe,
		input => api.updateProductGroupsForPointOfSaleNoThrow(input),
		config,
		...mutations
	)
}

export const getGetProductMenuOptions = (api: Api, { path, query }: EndpointInput<Endpoint.GetProductMenu>) => ({
	queryKey: [Endpoint.GetProductMenu, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getProductMenu({ path, query })
})

export const preloadGetProductMenu = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductMenu>
) => {
	return queryClient.ensureQueryData(getGetProductMenuOptions(api, input))
}

export const useGetProductMenu = (
	path: EndpointPath<Endpoint.GetProductMenu>,
	query: EndpointQuery<Endpoint.GetProductMenu>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductMenu>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductMenuOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.pointOfSaleUuid && (config?.enabled ?? true)
	})
}

export const getListAvailableProductsForSalesChannelOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.ListAvailableProductsForSalesChannel>
) => ({
	queryKey: [Endpoint.ListAvailableProductsForSalesChannel, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.listAvailableProductsForSalesChannel({ path, query })
})

export const preloadListAvailableProductsForSalesChannel = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListAvailableProductsForSalesChannel>
) => {
	return queryClient.ensureQueryData(getListAvailableProductsForSalesChannelOptions(api, input))
}

export const useListAvailableProductsForSalesChannel = (
	path: EndpointPath<Endpoint.ListAvailableProductsForSalesChannel>,
	query: EndpointQuery<Endpoint.ListAvailableProductsForSalesChannel>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListAvailableProductsForSalesChannel>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListAvailableProductsForSalesChannelOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.salesChannelUuid && (config?.enabled ?? true)
	})
}

export const usePaginatedListAvailableProductsForSalesChannel = (
	path: EndpointPath<Endpoint.ListAvailableProductsForSalesChannel>,
	query: EndpointQuery<Endpoint.ListAvailableProductsForSalesChannel>
) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [
			Endpoint.ListAvailableProductsForSalesChannel,
			createStableQueryKey(path),
			createStableQueryKey(query)
		],
		queryFn: ({ pageParam = 0 }) =>
			api.listAvailableProductsForSalesChannel({ path, query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const getGetProductFromBarcodeOptions = (api: Api, { path }: EndpointInput<Endpoint.GetProductFromBarcode>) => ({
	queryKey: [Endpoint.GetProductFromBarcode, createStableQueryKey(path)],
	queryFn: () => api.getProductFromBarcode({ path })
})

export const preloadGetProductFromBarcode = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductFromBarcode>
) => {
	return queryClient.ensureQueryData(getGetProductFromBarcodeOptions(api, input))
}

export const useGetProductFromBarcode = (
	path: EndpointPath<Endpoint.GetProductFromBarcode>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductFromBarcode>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductFromBarcodeOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.barcode && (config?.enabled ?? true)
	})
}

export const getListProductGroupsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListProductGroups>) => ({
	queryKey: [Endpoint.ListProductGroups, createStableQueryKey(query)],
	queryFn: () => api.listProductGroups({ query })
})

export const preloadListProductGroups = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListProductGroups>
) => {
	return queryClient.ensureQueryData(getListProductGroupsOptions(api, input))
}

export const useListProductGroups = (
	query: EndpointQuery<Endpoint.ListProductGroups>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListProductGroups>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListProductGroupsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListProductGroups = (query: EndpointQuery<Endpoint.ListProductGroups>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListProductGroups, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listProductGroups({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateProductGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProductGroup>,
	...mutations: Mutation<Endpoint.CreateProductGroup, any, any>[]
): EnhancedMutationApi<Endpoint.CreateProductGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateProductGroup, Subscribe>(
		subscribe,
		input => api.createProductGroupNoThrow(input),
		config,
		...mutations
	)
}

export const getGetProductGroupOptions = (api: Api, { path }: EndpointInput<Endpoint.GetProductGroup>) => ({
	queryKey: [Endpoint.GetProductGroup, createStableQueryKey(path)],
	queryFn: () => api.getProductGroup({ path })
})

export const preloadGetProductGroup = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductGroup>
) => {
	return queryClient.ensureQueryData(getGetProductGroupOptions(api, input))
}

export const useGetProductGroup = (
	path: EndpointPath<Endpoint.GetProductGroup>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductGroup>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductGroupOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.groupUuid && (config?.enabled ?? true)
	})
}

export const useBaseDeleteProductGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductGroup>,
	...mutations: Mutation<Endpoint.DeleteProductGroup, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteProductGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteProductGroup, Subscribe>(
		subscribe,
		input => api.deleteProductGroupNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateProductGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductGroup>,
	...mutations: Mutation<Endpoint.UpdateProductGroup, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateProductGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateProductGroup, Subscribe>(
		subscribe,
		input => api.updateProductGroupNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseSetProductGroupImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetProductGroupImage>,
	...mutations: Mutation<Endpoint.SetProductGroupImage, any, any>[]
): EnhancedMutationApi<Endpoint.SetProductGroupImage, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SetProductGroupImage, Subscribe>(
		subscribe,
		input => api.setProductGroupImageNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteProductGroupImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductGroupImage>,
	...mutations: Mutation<Endpoint.DeleteProductGroupImage, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteProductGroupImage, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteProductGroupImage, Subscribe>(
		subscribe,
		input => api.deleteProductGroupImageNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseAddProductsToProductGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.AddProductsToProductGroup>,
	...mutations: Mutation<Endpoint.AddProductsToProductGroup, any, any>[]
): EnhancedMutationApi<Endpoint.AddProductsToProductGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.AddProductsToProductGroup, Subscribe>(
		subscribe,
		input => api.addProductsToProductGroupNoThrow(input),
		config,
		...mutations
	)
}

export const getListProductGroupFeesOptions = (api: Api, { path }: EndpointInput<Endpoint.ListProductGroupFees>) => ({
	queryKey: [Endpoint.ListProductGroupFees, createStableQueryKey(path)],
	queryFn: () => api.listProductGroupFees({ path })
})

export const preloadListProductGroupFees = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListProductGroupFees>
) => {
	return queryClient.ensureQueryData(getListProductGroupFeesOptions(api, input))
}

export const useListProductGroupFees = (
	path: EndpointPath<Endpoint.ListProductGroupFees>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListProductGroupFees>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListProductGroupFeesOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.groupUuid && (config?.enabled ?? true)
	})
}

export const useBaseCreateProductGroupFee = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProductGroupFee>,
	...mutations: Mutation<Endpoint.CreateProductGroupFee, any, any>[]
): EnhancedMutationApi<Endpoint.CreateProductGroupFee, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateProductGroupFee, Subscribe>(
		subscribe,
		input => api.createProductGroupFeeNoThrow(input),
		config,
		...mutations
	)
}

export const getGetProductGroupFeeOptions = (api: Api, { path }: EndpointInput<Endpoint.GetProductGroupFee>) => ({
	queryKey: [Endpoint.GetProductGroupFee, createStableQueryKey(path)],
	queryFn: () => api.getProductGroupFee({ path })
})

export const preloadGetProductGroupFee = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductGroupFee>
) => {
	return queryClient.ensureQueryData(getGetProductGroupFeeOptions(api, input))
}

export const useGetProductGroupFee = (
	path: EndpointPath<Endpoint.GetProductGroupFee>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductGroupFee>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductGroupFeeOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.groupUuid && !!path.feeUuid && (config?.enabled ?? true)
	})
}

export const useBaseDeleteProductGroupFee = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductGroupFee>,
	...mutations: Mutation<Endpoint.DeleteProductGroupFee, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteProductGroupFee, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteProductGroupFee, Subscribe>(
		subscribe,
		input => api.deleteProductGroupFeeNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateProductGroupFee = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductGroupFee>,
	...mutations: Mutation<Endpoint.UpdateProductGroupFee, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateProductGroupFee, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateProductGroupFee, Subscribe>(
		subscribe,
		input => api.updateProductGroupFeeNoThrow(input),
		config,
		...mutations
	)
}

export const getListProductCategoriesOptions = (
	api: Api,
	{ query }: EndpointInput<Endpoint.ListProductCategories>
) => ({
	queryKey: [Endpoint.ListProductCategories, createStableQueryKey(query)],
	queryFn: () => api.listProductCategories({ query })
})

export const preloadListProductCategories = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListProductCategories>
) => {
	return queryClient.ensureQueryData(getListProductCategoriesOptions(api, input))
}

export const useListProductCategories = (
	query: EndpointQuery<Endpoint.ListProductCategories>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListProductCategories>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListProductCategoriesOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListProductCategories = (query: EndpointQuery<Endpoint.ListProductCategories>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListProductCategories, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listProductCategories({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateProductCategory = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProductCategory>,
	...mutations: Mutation<Endpoint.CreateProductCategory, any, any>[]
): EnhancedMutationApi<Endpoint.CreateProductCategory, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateProductCategory, Subscribe>(
		subscribe,
		input => api.createProductCategoryNoThrow(input),
		config,
		...mutations
	)
}

export const getGetProductCategoryHierarchyOptions = (
	api: Api,
	{}: EndpointInput<Endpoint.GetProductCategoryHierarchy>
) => ({
	queryKey: [Endpoint.GetProductCategoryHierarchy],
	queryFn: () => api.getProductCategoryHierarchy({})
})

export const preloadGetProductCategoryHierarchy = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductCategoryHierarchy>
) => {
	return queryClient.ensureQueryData(getGetProductCategoryHierarchyOptions(api, input))
}

export const useGetProductCategoryHierarchy = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductCategoryHierarchy>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductCategoryHierarchyOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetProductCategoryOptions = (api: Api, { path }: EndpointInput<Endpoint.GetProductCategory>) => ({
	queryKey: [Endpoint.GetProductCategory, createStableQueryKey(path)],
	queryFn: () => api.getProductCategory({ path })
})

export const preloadGetProductCategory = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductCategory>
) => {
	return queryClient.ensureQueryData(getGetProductCategoryOptions(api, input))
}

export const useGetProductCategory = (
	path: EndpointPath<Endpoint.GetProductCategory>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductCategory>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductCategoryOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.categoryUuid && (config?.enabled ?? true)
	})
}

export const useBaseDeleteProductCategory = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductCategory>,
	...mutations: Mutation<Endpoint.DeleteProductCategory, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteProductCategory, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteProductCategory, Subscribe>(
		subscribe,
		input => api.deleteProductCategoryNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateProductCategory = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductCategory>,
	...mutations: Mutation<Endpoint.UpdateProductCategory, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateProductCategory, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateProductCategory, Subscribe>(
		subscribe,
		input => api.updateProductCategoryNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseSetProductCategoryImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetProductCategoryImage>,
	...mutations: Mutation<Endpoint.SetProductCategoryImage, any, any>[]
): EnhancedMutationApi<Endpoint.SetProductCategoryImage, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SetProductCategoryImage, Subscribe>(
		subscribe,
		input => api.setProductCategoryImageNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteProductCategoryImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductCategoryImage>,
	...mutations: Mutation<Endpoint.DeleteProductCategoryImage, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteProductCategoryImage, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteProductCategoryImage, Subscribe>(
		subscribe,
		input => api.deleteProductCategoryImageNoThrow(input),
		config,
		...mutations
	)
}

export const getGetProductOptions = (api: Api, { path }: EndpointInput<Endpoint.GetProduct>) => ({
	queryKey: [Endpoint.GetProduct, createStableQueryKey(path)],
	queryFn: () => api.getProduct({ path })
})

export const preloadGetProduct = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetProduct>) => {
	return queryClient.ensureQueryData(getGetProductOptions(api, input))
}

export const useGetProduct = (
	path: EndpointPath<Endpoint.GetProduct>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProduct>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.productUuid && (config?.enabled ?? true)
	})
}

export const useBaseDeleteProduct = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProduct>,
	...mutations: Mutation<Endpoint.DeleteProduct, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteProduct, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteProduct, Subscribe>(
		subscribe,
		input => api.deleteProductNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateProduct = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProduct>,
	...mutations: Mutation<Endpoint.UpdateProduct, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateProduct, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateProduct, Subscribe>(
		subscribe,
		input => api.updateProductNoThrow(input),
		config,
		...mutations
	)
}

export const getGetProductEditOptions = (api: Api, { path }: EndpointInput<Endpoint.GetProductEdit>) => ({
	queryKey: [Endpoint.GetProductEdit, createStableQueryKey(path)],
	queryFn: () => api.getProductEdit({ path })
})

export const preloadGetProductEdit = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductEdit>
) => {
	return queryClient.ensureQueryData(getGetProductEditOptions(api, input))
}

export const useGetProductEdit = (
	path: EndpointPath<Endpoint.GetProductEdit>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductEdit>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductEditOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.productUuid && (config?.enabled ?? true)
	})
}

export const getListProductBarcodesOptions = (api: Api, { path }: EndpointInput<Endpoint.ListProductBarcodes>) => ({
	queryKey: [Endpoint.ListProductBarcodes, createStableQueryKey(path)],
	queryFn: () => api.listProductBarcodes({ path })
})

export const preloadListProductBarcodes = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListProductBarcodes>
) => {
	return queryClient.ensureQueryData(getListProductBarcodesOptions(api, input))
}

export const useListProductBarcodes = (
	path: EndpointPath<Endpoint.ListProductBarcodes>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListProductBarcodes>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListProductBarcodesOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.productUuid && (config?.enabled ?? true)
	})
}

export const useBaseCreateProductBarcode = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProductBarcode>,
	...mutations: Mutation<Endpoint.CreateProductBarcode, any, any>[]
): EnhancedMutationApi<Endpoint.CreateProductBarcode, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateProductBarcode, Subscribe>(
		subscribe,
		input => api.createProductBarcodeNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteProductBarcode = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductBarcode>,
	...mutations: Mutation<Endpoint.DeleteProductBarcode, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteProductBarcode, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteProductBarcode, Subscribe>(
		subscribe,
		input => api.deleteProductBarcodeNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCreateProductUnitOfMeasure = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProductUnitOfMeasure>,
	...mutations: Mutation<Endpoint.CreateProductUnitOfMeasure, any, any>[]
): EnhancedMutationApi<Endpoint.CreateProductUnitOfMeasure, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateProductUnitOfMeasure, Subscribe>(
		subscribe,
		input => api.createProductUnitOfMeasureNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseSetProductBaseUnit = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetProductBaseUnit>,
	...mutations: Mutation<Endpoint.SetProductBaseUnit, any, any>[]
): EnhancedMutationApi<Endpoint.SetProductBaseUnit, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SetProductBaseUnit, Subscribe>(
		subscribe,
		input => api.setProductBaseUnitNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateProductUnitOfMeasure = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductUnitOfMeasure>,
	...mutations: Mutation<Endpoint.UpdateProductUnitOfMeasure, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateProductUnitOfMeasure, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateProductUnitOfMeasure, Subscribe>(
		subscribe,
		input => api.updateProductUnitOfMeasureNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteProductUnitOfMeasure = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductUnitOfMeasure>,
	...mutations: Mutation<Endpoint.DeleteProductUnitOfMeasure, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteProductUnitOfMeasure, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteProductUnitOfMeasure, Subscribe>(
		subscribe,
		input => api.deleteProductUnitOfMeasureNoThrow(input),
		config,
		...mutations
	)
}

export const getGetProductPricingOptions = (api: Api, { path }: EndpointInput<Endpoint.GetProductPricing>) => ({
	queryKey: [Endpoint.GetProductPricing, createStableQueryKey(path)],
	queryFn: () => api.getProductPricing({ path })
})

export const preloadGetProductPricing = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductPricing>
) => {
	return queryClient.ensureQueryData(getGetProductPricingOptions(api, input))
}

export const useGetProductPricing = (
	path: EndpointPath<Endpoint.GetProductPricing>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductPricing>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductPricingOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.productUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateProductPricing = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductPricing>,
	...mutations: Mutation<Endpoint.UpdateProductPricing, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateProductPricing, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateProductPricing, Subscribe>(
		subscribe,
		input => api.updateProductPricingNoThrow(input),
		config,
		...mutations
	)
}

export const getGetProductOrganizationOptions = (
	api: Api,
	{ path }: EndpointInput<Endpoint.GetProductOrganization>
) => ({
	queryKey: [Endpoint.GetProductOrganization, createStableQueryKey(path)],
	queryFn: () => api.getProductOrganization({ path })
})

export const preloadGetProductOrganization = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductOrganization>
) => {
	return queryClient.ensureQueryData(getGetProductOrganizationOptions(api, input))
}

export const useGetProductOrganization = (
	path: EndpointPath<Endpoint.GetProductOrganization>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductOrganization>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductOrganizationOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.productUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateProductOrganization = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductOrganization>,
	...mutations: Mutation<Endpoint.UpdateProductOrganization, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateProductOrganization, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateProductOrganization, Subscribe>(
		subscribe,
		input => api.updateProductOrganizationNoThrow(input),
		config,
		...mutations
	)
}

export const getListProductImagesOptions = (api: Api, { path }: EndpointInput<Endpoint.ListProductImages>) => ({
	queryKey: [Endpoint.ListProductImages, createStableQueryKey(path)],
	queryFn: () => api.listProductImages({ path })
})

export const preloadListProductImages = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListProductImages>
) => {
	return queryClient.ensureQueryData(getListProductImagesOptions(api, input))
}

export const useListProductImages = (
	path: EndpointPath<Endpoint.ListProductImages>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListProductImages>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListProductImagesOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.productUuid && (config?.enabled ?? true)
	})
}

export const useBaseAttachProductImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.AttachProductImage>,
	...mutations: Mutation<Endpoint.AttachProductImage, any, any>[]
): EnhancedMutationApi<Endpoint.AttachProductImage, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.AttachProductImage, Subscribe>(
		subscribe,
		input => api.attachProductImageNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseReorderProductImages = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ReorderProductImages>,
	...mutations: Mutation<Endpoint.ReorderProductImages, any, any>[]
): EnhancedMutationApi<Endpoint.ReorderProductImages, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ReorderProductImages, Subscribe>(
		subscribe,
		input => api.reorderProductImagesNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteProductImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteProductImage>,
	...mutations: Mutation<Endpoint.DeleteProductImage, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteProductImage, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteProductImage, Subscribe>(
		subscribe,
		input => api.deleteProductImageNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseSetPrimaryProductImage = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetPrimaryProductImage>,
	...mutations: Mutation<Endpoint.SetPrimaryProductImage, any, any>[]
): EnhancedMutationApi<Endpoint.SetPrimaryProductImage, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SetPrimaryProductImage, Subscribe>(
		subscribe,
		input => api.setPrimaryProductImageNoThrow(input),
		config,
		...mutations
	)
}

export const getListProductVariantsOptions = (api: Api, { path }: EndpointInput<Endpoint.ListProductVariants>) => ({
	queryKey: [Endpoint.ListProductVariants, createStableQueryKey(path)],
	queryFn: () => api.listProductVariants({ path })
})

export const preloadListProductVariants = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListProductVariants>
) => {
	return queryClient.ensureQueryData(getListProductVariantsOptions(api, input))
}

export const useListProductVariants = (
	path: EndpointPath<Endpoint.ListProductVariants>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListProductVariants>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListProductVariantsOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.productUuid && (config?.enabled ?? true)
	})
}

export const useBaseCreateProductVariant = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateProductVariant>,
	...mutations: Mutation<Endpoint.CreateProductVariant, any, any>[]
): EnhancedMutationApi<Endpoint.CreateProductVariant, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateProductVariant, Subscribe>(
		subscribe,
		input => api.createProductVariantNoThrow(input),
		config,
		...mutations
	)
}

export const getGetProductVariantOptions = (api: Api, { path }: EndpointInput<Endpoint.GetProductVariant>) => ({
	queryKey: [Endpoint.GetProductVariant, createStableQueryKey(path)],
	queryFn: () => api.getProductVariant({ path })
})

export const preloadGetProductVariant = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductVariant>
) => {
	return queryClient.ensureQueryData(getGetProductVariantOptions(api, input))
}

export const useGetProductVariant = (
	path: EndpointPath<Endpoint.GetProductVariant>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductVariant>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductVariantOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.productUuid && !!path.variantUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateProductVariant = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateProductVariant>,
	...mutations: Mutation<Endpoint.UpdateProductVariant, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateProductVariant, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateProductVariant, Subscribe>(
		subscribe,
		input => api.updateProductVariantNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCreateVariantBarcode = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateVariantBarcode>,
	...mutations: Mutation<Endpoint.CreateVariantBarcode, any, any>[]
): EnhancedMutationApi<Endpoint.CreateVariantBarcode, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateVariantBarcode, Subscribe>(
		subscribe,
		input => api.createVariantBarcodeNoThrow(input),
		config,
		...mutations
	)
}

export const getListVariantBarcodesOptions = (api: Api, { path }: EndpointInput<Endpoint.ListVariantBarcodes>) => ({
	queryKey: [Endpoint.ListVariantBarcodes, createStableQueryKey(path)],
	queryFn: () => api.listVariantBarcodes({ path })
})

export const preloadListVariantBarcodes = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListVariantBarcodes>
) => {
	return queryClient.ensureQueryData(getListVariantBarcodesOptions(api, input))
}

export const useListVariantBarcodes = (
	path: EndpointPath<Endpoint.ListVariantBarcodes>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListVariantBarcodes>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListVariantBarcodesOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.productUuid && !!path.variantUuid && (config?.enabled ?? true)
	})
}

export const useBaseDeleteVariantBarcode = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteVariantBarcode>,
	...mutations: Mutation<Endpoint.DeleteVariantBarcode, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteVariantBarcode, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteVariantBarcode, Subscribe>(
		subscribe,
		input => api.deleteVariantBarcodeNoThrow(input),
		config,
		...mutations
	)
}

export const getGetVariantPricingOptions = (api: Api, { path }: EndpointInput<Endpoint.GetVariantPricing>) => ({
	queryKey: [Endpoint.GetVariantPricing, createStableQueryKey(path)],
	queryFn: () => api.getVariantPricing({ path })
})

export const preloadGetVariantPricing = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetVariantPricing>
) => {
	return queryClient.ensureQueryData(getGetVariantPricingOptions(api, input))
}

export const useGetVariantPricing = (
	path: EndpointPath<Endpoint.GetVariantPricing>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetVariantPricing>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetVariantPricingOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.productUuid && !!path.variantUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateVariantPricing = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateVariantPricing>,
	...mutations: Mutation<Endpoint.UpdateVariantPricing, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateVariantPricing, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateVariantPricing, Subscribe>(
		subscribe,
		input => api.updateVariantPricingNoThrow(input),
		config,
		...mutations
	)
}

export const getListUnitsOfMeasureOptions = (api: Api, {}: EndpointInput<Endpoint.ListUnitsOfMeasure>) => ({
	queryKey: [Endpoint.ListUnitsOfMeasure],
	queryFn: () => api.listUnitsOfMeasure({})
})

export const preloadListUnitsOfMeasure = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListUnitsOfMeasure>
) => {
	return queryClient.ensureQueryData(getListUnitsOfMeasureOptions(api, input))
}

export const useListUnitsOfMeasure = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListUnitsOfMeasure>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListUnitsOfMeasureOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreateUnitOfMeasure = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateUnitOfMeasure>,
	...mutations: Mutation<Endpoint.CreateUnitOfMeasure, any, any>[]
): EnhancedMutationApi<Endpoint.CreateUnitOfMeasure, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateUnitOfMeasure, Subscribe>(
		subscribe,
		input => api.createUnitOfMeasureNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateUnitOfMeasure = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateUnitOfMeasure>,
	...mutations: Mutation<Endpoint.UpdateUnitOfMeasure, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateUnitOfMeasure, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateUnitOfMeasure, Subscribe>(
		subscribe,
		input => api.updateUnitOfMeasureNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteUnitOfMeasure = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteUnitOfMeasure>,
	...mutations: Mutation<Endpoint.DeleteUnitOfMeasure, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteUnitOfMeasure, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteUnitOfMeasure, Subscribe>(
		subscribe,
		input => api.deleteUnitOfMeasureNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseExportProducts = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ExportProducts>,
	...mutations: Mutation<Endpoint.ExportProducts, any, any>[]
): EnhancedMutationApi<Endpoint.ExportProducts, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ExportProducts, Subscribe>(
		subscribe,
		input => api.exportProductsNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseImportProducts = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ImportProducts>,
	...mutations: Mutation<Endpoint.ImportProducts, any, any>[]
): EnhancedMutationApi<Endpoint.ImportProducts, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ImportProducts, Subscribe>(
		subscribe,
		input => api.importProductsNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseValidateProductImport = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ValidateProductImport>,
	...mutations: Mutation<Endpoint.ValidateProductImport, any, any>[]
): EnhancedMutationApi<Endpoint.ValidateProductImport, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ValidateProductImport, Subscribe>(
		subscribe,
		input => api.validateProductImportNoThrow(input),
		config,
		...mutations
	)
}

export const getListAssetsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListAssets>) => ({
	queryKey: [Endpoint.ListAssets, createStableQueryKey(query)],
	queryFn: () => api.listAssets({ query })
})

export const preloadListAssets = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.ListAssets>) => {
	return queryClient.ensureQueryData(getListAssetsOptions(api, input))
}

export const useListAssets = (
	query: EndpointQuery<Endpoint.ListAssets>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListAssets>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListAssetsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListAssets = (query: EndpointQuery<Endpoint.ListAssets>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListAssets, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listAssets({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseInitAssetUpload = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.InitAssetUpload>,
	...mutations: Mutation<Endpoint.InitAssetUpload, any, any>[]
): EnhancedMutationApi<Endpoint.InitAssetUpload, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.InitAssetUpload, Subscribe>(
		subscribe,
		input => api.initAssetUploadNoThrow(input),
		config,
		...mutations
	)
}

export const getListAssetTagsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListAssetTags>) => ({
	queryKey: [Endpoint.ListAssetTags, createStableQueryKey(query)],
	queryFn: () => api.listAssetTags({ query })
})

export const preloadListAssetTags = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListAssetTags>
) => {
	return queryClient.ensureQueryData(getListAssetTagsOptions(api, input))
}

export const useListAssetTags = (
	query: EndpointQuery<Endpoint.ListAssetTags>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListAssetTags>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListAssetTagsOptions(api, { query }),
		...(config ?? {}),
		enabled: !!query.category && (config?.enabled ?? true)
	})
}

export const useBaseCreateAssetTag = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateAssetTag>,
	...mutations: Mutation<Endpoint.CreateAssetTag, any, any>[]
): EnhancedMutationApi<Endpoint.CreateAssetTag, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateAssetTag, Subscribe>(
		subscribe,
		input => api.createAssetTagNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseRemoveTagFromAssets = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RemoveTagFromAssets>,
	...mutations: Mutation<Endpoint.RemoveTagFromAssets, any, any>[]
): EnhancedMutationApi<Endpoint.RemoveTagFromAssets, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RemoveTagFromAssets, Subscribe>(
		subscribe,
		input => api.removeTagFromAssetsNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseRenameAssetTag = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RenameAssetTag>,
	...mutations: Mutation<Endpoint.RenameAssetTag, any, any>[]
): EnhancedMutationApi<Endpoint.RenameAssetTag, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RenameAssetTag, Subscribe>(
		subscribe,
		input => api.renameAssetTagNoThrow(input),
		config,
		...mutations
	)
}

export const getGetAssetOptions = (api: Api, { path }: EndpointInput<Endpoint.GetAsset>) => ({
	queryKey: [Endpoint.GetAsset, createStableQueryKey(path)],
	queryFn: () => api.getAsset({ path })
})

export const preloadGetAsset = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetAsset>) => {
	return queryClient.ensureQueryData(getGetAssetOptions(api, input))
}

export const useGetAsset = (
	path: EndpointPath<Endpoint.GetAsset>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetAsset>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetAssetOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.assetUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateAsset = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateAsset>,
	...mutations: Mutation<Endpoint.UpdateAsset, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateAsset, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateAsset, Subscribe>(
		subscribe,
		input => api.updateAssetNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteAsset = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteAsset>,
	...mutations: Mutation<Endpoint.DeleteAsset, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteAsset, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteAsset, Subscribe>(
		subscribe,
		input => api.deleteAssetNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCompleteAssetUpload = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CompleteAssetUpload>,
	...mutations: Mutation<Endpoint.CompleteAssetUpload, any, any>[]
): EnhancedMutationApi<Endpoint.CompleteAssetUpload, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CompleteAssetUpload, Subscribe>(
		subscribe,
		input => api.completeAssetUploadNoThrow(input),
		config,
		...mutations
	)
}

export const getListMarketingChannelsOptions = (api: Api, {}: EndpointInput<Endpoint.ListMarketingChannels>) => ({
	queryKey: [Endpoint.ListMarketingChannels],
	queryFn: () => api.listMarketingChannels({})
})

export const preloadListMarketingChannels = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListMarketingChannels>
) => {
	return queryClient.ensureQueryData(getListMarketingChannelsOptions(api, input))
}

export const useListMarketingChannels = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListMarketingChannels>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListMarketingChannelsOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreateMarketingChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateMarketingChannel>,
	...mutations: Mutation<Endpoint.CreateMarketingChannel, any, any>[]
): EnhancedMutationApi<Endpoint.CreateMarketingChannel, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateMarketingChannel, Subscribe>(
		subscribe,
		input => api.createMarketingChannelNoThrow(input),
		config,
		...mutations
	)
}

export const getGetMarketingChannelOptions = (api: Api, { path }: EndpointInput<Endpoint.GetMarketingChannel>) => ({
	queryKey: [Endpoint.GetMarketingChannel, createStableQueryKey(path)],
	queryFn: () => api.getMarketingChannel({ path })
})

export const preloadGetMarketingChannel = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetMarketingChannel>
) => {
	return queryClient.ensureQueryData(getGetMarketingChannelOptions(api, input))
}

export const useGetMarketingChannel = (
	path: EndpointPath<Endpoint.GetMarketingChannel>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetMarketingChannel>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetMarketingChannelOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.marketingChannelUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateMarketingChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateMarketingChannel>,
	...mutations: Mutation<Endpoint.UpdateMarketingChannel, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateMarketingChannel, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateMarketingChannel, Subscribe>(
		subscribe,
		input => api.updateMarketingChannelNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteMarketingChannel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteMarketingChannel>,
	...mutations: Mutation<Endpoint.DeleteMarketingChannel, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteMarketingChannel, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteMarketingChannel, Subscribe>(
		subscribe,
		input => api.deleteMarketingChannelNoThrow(input),
		config,
		...mutations
	)
}

export const getGetFeedLayoutOptions = (api: Api, { path, query }: EndpointInput<Endpoint.GetFeedLayout>) => ({
	queryKey: [Endpoint.GetFeedLayout, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getFeedLayout({ path, query })
})

export const preloadGetFeedLayout = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetFeedLayout>
) => {
	return queryClient.ensureQueryData(getGetFeedLayoutOptions(api, input))
}

export const useGetFeedLayout = (
	path: EndpointPath<Endpoint.GetFeedLayout>,
	query: EndpointQuery<Endpoint.GetFeedLayout>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetFeedLayout>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetFeedLayoutOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.marketingChannelUuid && (config?.enabled ?? true)
	})
}

export const useBaseReplaceFeedLayoutBlocks = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ReplaceFeedLayoutBlocks>,
	...mutations: Mutation<Endpoint.ReplaceFeedLayoutBlocks, any, any>[]
): EnhancedMutationApi<Endpoint.ReplaceFeedLayoutBlocks, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ReplaceFeedLayoutBlocks, Subscribe>(
		subscribe,
		input => api.replaceFeedLayoutBlocksNoThrow(input),
		config,
		...mutations
	)
}

export const getGetChannelLayoutOptions = (api: Api, { path, query }: EndpointInput<Endpoint.GetChannelLayout>) => ({
	queryKey: [Endpoint.GetChannelLayout, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getChannelLayout({ path, query })
})

export const preloadGetChannelLayout = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetChannelLayout>
) => {
	return queryClient.ensureQueryData(getGetChannelLayoutOptions(api, input))
}

export const useGetChannelLayout = (
	path: EndpointPath<Endpoint.GetChannelLayout>,
	query: EndpointQuery<Endpoint.GetChannelLayout>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetChannelLayout>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetChannelLayoutOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.marketingChannelUuid && (config?.enabled ?? true)
	})
}

export const useBaseReplaceChannelLayout = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ReplaceChannelLayout>,
	...mutations: Mutation<Endpoint.ReplaceChannelLayout, any, any>[]
): EnhancedMutationApi<Endpoint.ReplaceChannelLayout, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ReplaceChannelLayout, Subscribe>(
		subscribe,
		input => api.replaceChannelLayoutNoThrow(input),
		config,
		...mutations
	)
}

export const getGetChannelResolvedContentOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.GetChannelResolvedContent>
) => ({
	queryKey: [Endpoint.GetChannelResolvedContent, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getChannelResolvedContent({ path, query })
})

export const preloadGetChannelResolvedContent = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetChannelResolvedContent>
) => {
	return queryClient.ensureQueryData(getGetChannelResolvedContentOptions(api, input))
}

export const useGetChannelResolvedContent = (
	path: EndpointPath<Endpoint.GetChannelResolvedContent>,
	query: EndpointQuery<Endpoint.GetChannelResolvedContent>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetChannelResolvedContent>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetChannelResolvedContentOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.marketingChannelUuid && (config?.enabled ?? true)
	})
}

export const getListMarketingContentOptions = (api: Api, { query }: EndpointInput<Endpoint.ListMarketingContent>) => ({
	queryKey: [Endpoint.ListMarketingContent, createStableQueryKey(query)],
	queryFn: () => api.listMarketingContent({ query })
})

export const preloadListMarketingContent = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListMarketingContent>
) => {
	return queryClient.ensureQueryData(getListMarketingContentOptions(api, input))
}

export const useListMarketingContent = (
	query: EndpointQuery<Endpoint.ListMarketingContent>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListMarketingContent>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListMarketingContentOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListMarketingContent = (query: EndpointQuery<Endpoint.ListMarketingContent>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListMarketingContent, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listMarketingContent({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateMarketingContent = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateMarketingContent>,
	...mutations: Mutation<Endpoint.CreateMarketingContent, any, any>[]
): EnhancedMutationApi<Endpoint.CreateMarketingContent, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateMarketingContent, Subscribe>(
		subscribe,
		input => api.createMarketingContentNoThrow(input),
		config,
		...mutations
	)
}

export const getGetMarketingContentOptions = (api: Api, { path }: EndpointInput<Endpoint.GetMarketingContent>) => ({
	queryKey: [Endpoint.GetMarketingContent, createStableQueryKey(path)],
	queryFn: () => api.getMarketingContent({ path })
})

export const preloadGetMarketingContent = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetMarketingContent>
) => {
	return queryClient.ensureQueryData(getGetMarketingContentOptions(api, input))
}

export const useGetMarketingContent = (
	path: EndpointPath<Endpoint.GetMarketingContent>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetMarketingContent>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetMarketingContentOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.contentUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateMarketingContent = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateMarketingContent>,
	...mutations: Mutation<Endpoint.UpdateMarketingContent, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateMarketingContent, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateMarketingContent, Subscribe>(
		subscribe,
		input => api.updateMarketingContentNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteMarketingContent = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteMarketingContent>,
	...mutations: Mutation<Endpoint.DeleteMarketingContent, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteMarketingContent, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteMarketingContent, Subscribe>(
		subscribe,
		input => api.deleteMarketingContentNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseReplaceMarketingContentBlocks = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ReplaceMarketingContentBlocks>,
	...mutations: Mutation<Endpoint.ReplaceMarketingContentBlocks, any, any>[]
): EnhancedMutationApi<Endpoint.ReplaceMarketingContentBlocks, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ReplaceMarketingContentBlocks, Subscribe>(
		subscribe,
		input => api.replaceMarketingContentBlocksNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDuplicateMarketingContent = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DuplicateMarketingContent>,
	...mutations: Mutation<Endpoint.DuplicateMarketingContent, any, any>[]
): EnhancedMutationApi<Endpoint.DuplicateMarketingContent, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DuplicateMarketingContent, Subscribe>(
		subscribe,
		input => api.duplicateMarketingContentNoThrow(input),
		config,
		...mutations
	)
}

export const getListOrdersOptions = (api: Api, { query }: EndpointInput<Endpoint.ListOrders>) => ({
	queryKey: [Endpoint.ListOrders, createStableQueryKey(query)],
	queryFn: () => api.listOrders({ query })
})

export const preloadListOrders = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.ListOrders>) => {
	return queryClient.ensureQueryData(getListOrdersOptions(api, input))
}

export const useListOrders = (
	query: EndpointQuery<Endpoint.ListOrders>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListOrders>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListOrdersOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListOrders = (query: EndpointQuery<Endpoint.ListOrders>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListOrders, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listOrders({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const getGetOrderOptions = (api: Api, { path }: EndpointInput<Endpoint.GetOrder>) => ({
	queryKey: [Endpoint.GetOrder, createStableQueryKey(path)],
	queryFn: () => api.getOrder({ path })
})

export const preloadGetOrder = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetOrder>) => {
	return queryClient.ensureQueryData(getGetOrderOptions(api, input))
}

export const useGetOrder = (
	path: EndpointPath<Endpoint.GetOrder>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetOrder>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetOrderOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.orderUuid && (config?.enabled ?? true)
	})
}

export const useBaseRefundOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RefundOrder>,
	...mutations: Mutation<Endpoint.RefundOrder, any, any>[]
): EnhancedMutationApi<Endpoint.RefundOrder, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RefundOrder, Subscribe>(
		subscribe,
		input => api.refundOrderNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCancelOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CancelOrder>,
	...mutations: Mutation<Endpoint.CancelOrder, any, any>[]
): EnhancedMutationApi<Endpoint.CancelOrder, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CancelOrder, Subscribe>(
		subscribe,
		input => api.cancelOrderNoThrow(input),
		config,
		...mutations
	)
}

export const getGetExitCodeOptions = (api: Api, { path }: EndpointInput<Endpoint.GetExitCode>) => ({
	queryKey: [Endpoint.GetExitCode, createStableQueryKey(path)],
	queryFn: () => api.getExitCode({ path })
})

export const preloadGetExitCode = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetExitCode>
) => {
	return queryClient.ensureQueryData(getGetExitCodeOptions(api, input))
}

export const useGetExitCode = (
	path: EndpointPath<Endpoint.GetExitCode>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetExitCode>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetExitCodeOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.orderUuid && (config?.enabled ?? true)
	})
}

export const useBaseGetOrderReceipt = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.GetOrderReceipt>,
	...mutations: Mutation<Endpoint.GetOrderReceipt, any, any>[]
): EnhancedMutationApi<Endpoint.GetOrderReceipt, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.GetOrderReceipt, Subscribe>(
		subscribe,
		input => api.getOrderReceiptNoThrow(input),
		config,
		...mutations
	)
}

export const getGetOrderReceiptUrlOptions = (api: Api, { path }: EndpointInput<Endpoint.GetOrderReceiptUrl>) => ({
	queryKey: [Endpoint.GetOrderReceiptUrl, createStableQueryKey(path)],
	queryFn: () => api.getOrderReceiptUrl({ path })
})

export const preloadGetOrderReceiptUrl = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetOrderReceiptUrl>
) => {
	return queryClient.ensureQueryData(getGetOrderReceiptUrlOptions(api, input))
}

export const useGetOrderReceiptUrl = (
	path: EndpointPath<Endpoint.GetOrderReceiptUrl>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetOrderReceiptUrl>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetOrderReceiptUrlOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.orderUuid && (config?.enabled ?? true)
	})
}

export const useBaseInvalidateOrderReceipt = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.InvalidateOrderReceipt>,
	...mutations: Mutation<Endpoint.InvalidateOrderReceipt, any, any>[]
): EnhancedMutationApi<Endpoint.InvalidateOrderReceipt, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.InvalidateOrderReceipt, Subscribe>(
		subscribe,
		input => api.invalidateOrderReceiptNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCreateOrderReceiptLink = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateOrderReceiptLink>,
	...mutations: Mutation<Endpoint.CreateOrderReceiptLink, any, any>[]
): EnhancedMutationApi<Endpoint.CreateOrderReceiptLink, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateOrderReceiptLink, Subscribe>(
		subscribe,
		input => api.createOrderReceiptLinkNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateFulfillmentStatus = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateFulfillmentStatus>,
	...mutations: Mutation<Endpoint.UpdateFulfillmentStatus, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateFulfillmentStatus, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateFulfillmentStatus, Subscribe>(
		subscribe,
		input => api.updateFulfillmentStatusNoThrow(input),
		config,
		...mutations
	)
}

export const getGetOrderTransactionOptions = (api: Api, { path }: EndpointInput<Endpoint.GetOrderTransaction>) => ({
	queryKey: [Endpoint.GetOrderTransaction, createStableQueryKey(path)],
	queryFn: () => api.getOrderTransaction({ path })
})

export const preloadGetOrderTransaction = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetOrderTransaction>
) => {
	return queryClient.ensureQueryData(getGetOrderTransactionOptions(api, input))
}

export const useGetOrderTransaction = (
	path: EndpointPath<Endpoint.GetOrderTransaction>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetOrderTransaction>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetOrderTransactionOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.orderUuid && !!path.transactionUuid && (config?.enabled ?? true)
	})
}

export const useBaseExportOrders = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ExportOrders>,
	...mutations: Mutation<Endpoint.ExportOrders, any, any>[]
): EnhancedMutationApi<Endpoint.ExportOrders, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ExportOrders, Subscribe>(
		subscribe,
		input => api.exportOrdersNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCreateBagCheck = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateBagCheck>,
	...mutations: Mutation<Endpoint.CreateBagCheck, any, any>[]
): EnhancedMutationApi<Endpoint.CreateBagCheck, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateBagCheck, Subscribe>(
		subscribe,
		input => api.createBagCheckNoThrow(input),
		config,
		...mutations
	)
}

export const getGetBagCheckOptions = (api: Api, { path }: EndpointInput<Endpoint.GetBagCheck>) => ({
	queryKey: [Endpoint.GetBagCheck, createStableQueryKey(path)],
	queryFn: () => api.getBagCheck({ path })
})

export const preloadGetBagCheck = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetBagCheck>
) => {
	return queryClient.ensureQueryData(getGetBagCheckOptions(api, input))
}

export const useGetBagCheck = (
	path: EndpointPath<Endpoint.GetBagCheck>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetBagCheck>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetBagCheckOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.checkUuid && (config?.enabled ?? true)
	})
}

export const useBaseBagCheckAddItem = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.BagCheckAddItem>,
	...mutations: Mutation<Endpoint.BagCheckAddItem, any, any>[]
): EnhancedMutationApi<Endpoint.BagCheckAddItem, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.BagCheckAddItem, Subscribe>(
		subscribe,
		input => api.bagCheckAddItemNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseBagCheckUpdateItemQuantity = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.BagCheckUpdateItemQuantity>,
	...mutations: Mutation<Endpoint.BagCheckUpdateItemQuantity, any, any>[]
): EnhancedMutationApi<Endpoint.BagCheckUpdateItemQuantity, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.BagCheckUpdateItemQuantity, Subscribe>(
		subscribe,
		input => api.bagCheckUpdateItemQuantityNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseRestartBagCheck = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RestartBagCheck>,
	...mutations: Mutation<Endpoint.RestartBagCheck, any, any>[]
): EnhancedMutationApi<Endpoint.RestartBagCheck, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RestartBagCheck, Subscribe>(
		subscribe,
		input => api.restartBagCheckNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseBagCheckApprove = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.BagCheckApprove>,
	...mutations: Mutation<Endpoint.BagCheckApprove, any, any>[]
): EnhancedMutationApi<Endpoint.BagCheckApprove, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.BagCheckApprove, Subscribe>(
		subscribe,
		input => api.bagCheckApproveNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseBagCheckReject = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.BagCheckReject>,
	...mutations: Mutation<Endpoint.BagCheckReject, any, any>[]
): EnhancedMutationApi<Endpoint.BagCheckReject, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.BagCheckReject, Subscribe>(
		subscribe,
		input => api.bagCheckRejectNoThrow(input),
		config,
		...mutations
	)
}

export const getListPromotionsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListPromotions>) => ({
	queryKey: [Endpoint.ListPromotions, createStableQueryKey(query)],
	queryFn: () => api.listPromotions({ query })
})

export const preloadListPromotions = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListPromotions>
) => {
	return queryClient.ensureQueryData(getListPromotionsOptions(api, input))
}

export const useListPromotions = (
	query: EndpointQuery<Endpoint.ListPromotions>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListPromotions>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListPromotionsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreatePromotion = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePromotion>,
	...mutations: Mutation<Endpoint.CreatePromotion, any, any>[]
): EnhancedMutationApi<Endpoint.CreatePromotion, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreatePromotion, Subscribe>(
		subscribe,
		input => api.createPromotionNoThrow(input),
		config,
		...mutations
	)
}

export const getGetPromotionOptions = (api: Api, { path }: EndpointInput<Endpoint.GetPromotion>) => ({
	queryKey: [Endpoint.GetPromotion, createStableQueryKey(path)],
	queryFn: () => api.getPromotion({ path })
})

export const preloadGetPromotion = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetPromotion>
) => {
	return queryClient.ensureQueryData(getGetPromotionOptions(api, input))
}

export const useGetPromotion = (
	path: EndpointPath<Endpoint.GetPromotion>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetPromotion>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetPromotionOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.promotionUuid && (config?.enabled ?? true)
	})
}

export const useBaseDeletePromotion = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeletePromotion>,
	...mutations: Mutation<Endpoint.DeletePromotion, any, any>[]
): EnhancedMutationApi<Endpoint.DeletePromotion, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeletePromotion, Subscribe>(
		subscribe,
		input => api.deletePromotionNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdatePromotion = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdatePromotion>,
	...mutations: Mutation<Endpoint.UpdatePromotion, any, any>[]
): EnhancedMutationApi<Endpoint.UpdatePromotion, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdatePromotion, Subscribe>(
		subscribe,
		input => api.updatePromotionNoThrow(input),
		config,
		...mutations
	)
}

export const getListPromotionGroupsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListPromotionGroups>) => ({
	queryKey: [Endpoint.ListPromotionGroups, createStableQueryKey(query)],
	queryFn: () => api.listPromotionGroups({ query })
})

export const preloadListPromotionGroups = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListPromotionGroups>
) => {
	return queryClient.ensureQueryData(getListPromotionGroupsOptions(api, input))
}

export const useListPromotionGroups = (
	query: EndpointQuery<Endpoint.ListPromotionGroups>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListPromotionGroups>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListPromotionGroupsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListPromotionGroups = (query: EndpointQuery<Endpoint.ListPromotionGroups>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListPromotionGroups, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listPromotionGroups({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const getListCountriesOptions = (api: Api, {}: EndpointInput<Endpoint.ListCountries>) => ({
	queryKey: [Endpoint.ListCountries],
	queryFn: () => api.listCountries({})
})

export const preloadListCountries = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListCountries>
) => {
	return queryClient.ensureQueryData(getListCountriesOptions(api, input))
}

export const useListCountries = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListCountries>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getListCountriesOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetCountryOptions = (api: Api, { path }: EndpointInput<Endpoint.GetCountry>) => ({
	queryKey: [Endpoint.GetCountry, createStableQueryKey(path)],
	queryFn: () => api.getCountry({ path })
})

export const preloadGetCountry = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetCountry>) => {
	return queryClient.ensureQueryData(getGetCountryOptions(api, input))
}

export const useGetCountry = (
	path: EndpointPath<Endpoint.GetCountry>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetCountry>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetCountryOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.countryCode && (config?.enabled ?? true)
	})
}

export const getListCountryTaxRatesOptions = (api: Api, { path }: EndpointInput<Endpoint.ListCountryTaxRates>) => ({
	queryKey: [Endpoint.ListCountryTaxRates, createStableQueryKey(path)],
	queryFn: () => api.listCountryTaxRates({ path })
})

export const preloadListCountryTaxRates = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListCountryTaxRates>
) => {
	return queryClient.ensureQueryData(getListCountryTaxRatesOptions(api, input))
}

export const useListCountryTaxRates = (
	path: EndpointPath<Endpoint.ListCountryTaxRates>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListCountryTaxRates>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListCountryTaxRatesOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.countryCode && (config?.enabled ?? true)
	})
}

export const getListTaxRatesOptions = (api: Api, {}: EndpointInput<Endpoint.ListTaxRates>) => ({
	queryKey: [Endpoint.ListTaxRates],
	queryFn: () => api.listTaxRates({})
})

export const preloadListTaxRates = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListTaxRates>
) => {
	return queryClient.ensureQueryData(getListTaxRatesOptions(api, input))
}

export const useListTaxRates = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListTaxRates>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getListTaxRatesOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetAvailableExportsOptions = (api: Api, {}: EndpointInput<Endpoint.GetAvailableExports>) => ({
	queryKey: [Endpoint.GetAvailableExports],
	queryFn: () => api.getAvailableExports({})
})

export const preloadGetAvailableExports = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetAvailableExports>
) => {
	return queryClient.ensureQueryData(getGetAvailableExportsOptions(api, input))
}

export const useGetAvailableExports = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetAvailableExports>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetAvailableExportsOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBasePricerProductExport = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.PricerProductExport>,
	...mutations: Mutation<Endpoint.PricerProductExport, any, any>[]
): EnhancedMutationApi<Endpoint.PricerProductExport, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.PricerProductExport, Subscribe>(
		subscribe,
		input => api.pricerProductExportNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseVismaExport = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.VismaExport>,
	...mutations: Mutation<Endpoint.VismaExport, any, any>[]
): EnhancedMutationApi<Endpoint.VismaExport, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.VismaExport, Subscribe>(
		subscribe,
		input => api.vismaExportNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDigiScaleProductExport = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DigiScaleProductExport>,
	...mutations: Mutation<Endpoint.DigiScaleProductExport, any, any>[]
): EnhancedMutationApi<Endpoint.DigiScaleProductExport, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DigiScaleProductExport, Subscribe>(
		subscribe,
		input => api.digiScaleProductExportNoThrow(input),
		config,
		...mutations
	)
}

export const getGetSAFTExportInfoOptions = (api: Api, {}: EndpointInput<Endpoint.GetSAFTExportInfo>) => ({
	queryKey: [Endpoint.GetSAFTExportInfo],
	queryFn: () => api.getSAFTExportInfo({})
})

export const preloadGetSAFTExportInfo = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetSAFTExportInfo>
) => {
	return queryClient.ensureQueryData(getGetSAFTExportInfoOptions(api, input))
}

export const useGetSAFTExportInfo = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetSAFTExportInfo>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetSAFTExportInfoOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseSAFTExportCashRegister = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SAFTExportCashRegister>,
	...mutations: Mutation<Endpoint.SAFTExportCashRegister, any, any>[]
): EnhancedMutationApi<Endpoint.SAFTExportCashRegister, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SAFTExportCashRegister, Subscribe>(
		subscribe,
		input => api.sAFTExportCashRegisterNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseSAFTExportFinancial = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SAFTExportFinancial>,
	...mutations: Mutation<Endpoint.SAFTExportFinancial, any, any>[]
): EnhancedMutationApi<Endpoint.SAFTExportFinancial, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SAFTExportFinancial, Subscribe>(
		subscribe,
		input => api.sAFTExportFinancialNoThrow(input),
		config,
		...mutations
	)
}

export const getListGiftCardsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListGiftCards>) => ({
	queryKey: [Endpoint.ListGiftCards, createStableQueryKey(query)],
	queryFn: () => api.listGiftCards({ query })
})

export const preloadListGiftCards = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListGiftCards>
) => {
	return queryClient.ensureQueryData(getListGiftCardsOptions(api, input))
}

export const useListGiftCards = (
	query: EndpointQuery<Endpoint.ListGiftCards>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListGiftCards>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListGiftCardsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListGiftCards = (query: EndpointQuery<Endpoint.ListGiftCards>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListGiftCards, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listGiftCards({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateGiftCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateGiftCard>,
	...mutations: Mutation<Endpoint.CreateGiftCard, any, any>[]
): EnhancedMutationApi<Endpoint.CreateGiftCard, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateGiftCard, Subscribe>(
		subscribe,
		input => api.createGiftCardNoThrow(input),
		config,
		...mutations
	)
}

export const getGetGiftCardOptions = (api: Api, { path }: EndpointInput<Endpoint.GetGiftCard>) => ({
	queryKey: [Endpoint.GetGiftCard, createStableQueryKey(path)],
	queryFn: () => api.getGiftCard({ path })
})

export const preloadGetGiftCard = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetGiftCard>
) => {
	return queryClient.ensureQueryData(getGetGiftCardOptions(api, input))
}

export const useGetGiftCard = (
	path: EndpointPath<Endpoint.GetGiftCard>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetGiftCard>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetGiftCardOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.giftCardUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateGiftCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateGiftCard>,
	...mutations: Mutation<Endpoint.UpdateGiftCard, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateGiftCard, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateGiftCard, Subscribe>(
		subscribe,
		input => api.updateGiftCardNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteGiftCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteGiftCard>,
	...mutations: Mutation<Endpoint.DeleteGiftCard, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteGiftCard, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteGiftCard, Subscribe>(
		subscribe,
		input => api.deleteGiftCardNoThrow(input),
		config,
		...mutations
	)
}

export const getListCompaniesOptions = (api: Api, { query }: EndpointInput<Endpoint.ListCompanies>) => ({
	queryKey: [Endpoint.ListCompanies, createStableQueryKey(query)],
	queryFn: () => api.listCompanies({ query })
})

export const preloadListCompanies = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListCompanies>
) => {
	return queryClient.ensureQueryData(getListCompaniesOptions(api, input))
}

export const useListCompanies = (
	query: EndpointQuery<Endpoint.ListCompanies>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListCompanies>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListCompaniesOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListCompanies = (query: EndpointQuery<Endpoint.ListCompanies>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListCompanies, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listCompanies({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateCompany = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateCompany>,
	...mutations: Mutation<Endpoint.CreateCompany, any, any>[]
): EnhancedMutationApi<Endpoint.CreateCompany, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateCompany, Subscribe>(
		subscribe,
		input => api.createCompanyNoThrow(input),
		config,
		...mutations
	)
}

export const getGetCompanyOptions = (api: Api, { path }: EndpointInput<Endpoint.GetCompany>) => ({
	queryKey: [Endpoint.GetCompany, createStableQueryKey(path)],
	queryFn: () => api.getCompany({ path })
})

export const preloadGetCompany = ({ api, queryClient }: PreloadContext, input: EndpointInput<Endpoint.GetCompany>) => {
	return queryClient.ensureQueryData(getGetCompanyOptions(api, input))
}

export const useGetCompany = (
	path: EndpointPath<Endpoint.GetCompany>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetCompany>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetCompanyOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.companyUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateCompany = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateCompany>,
	...mutations: Mutation<Endpoint.UpdateCompany, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateCompany, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateCompany, Subscribe>(
		subscribe,
		input => api.updateCompanyNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteCompany = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteCompany>,
	...mutations: Mutation<Endpoint.DeleteCompany, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteCompany, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteCompany, Subscribe>(
		subscribe,
		input => api.deleteCompanyNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCreateBusinessCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateBusinessCard>,
	...mutations: Mutation<Endpoint.CreateBusinessCard, any, any>[]
): EnhancedMutationApi<Endpoint.CreateBusinessCard, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateBusinessCard, Subscribe>(
		subscribe,
		input => api.createBusinessCardNoThrow(input),
		config,
		...mutations
	)
}

export const getListCompanySettlementsOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.ListCompanySettlements>
) => ({
	queryKey: [Endpoint.ListCompanySettlements, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.listCompanySettlements({ path, query })
})

export const preloadListCompanySettlements = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListCompanySettlements>
) => {
	return queryClient.ensureQueryData(getListCompanySettlementsOptions(api, input))
}

export const useListCompanySettlements = (
	path: EndpointPath<Endpoint.ListCompanySettlements>,
	query: EndpointQuery<Endpoint.ListCompanySettlements>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListCompanySettlements>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListCompanySettlementsOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.companyUuid && (config?.enabled ?? true)
	})
}

export const usePaginatedListCompanySettlements = (
	path: EndpointPath<Endpoint.ListCompanySettlements>,
	query: EndpointQuery<Endpoint.ListCompanySettlements>
) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListCompanySettlements, createStableQueryKey(path), createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listCompanySettlements({ path, query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateSettlement = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSettlement>,
	...mutations: Mutation<Endpoint.CreateSettlement, any, any>[]
): EnhancedMutationApi<Endpoint.CreateSettlement, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateSettlement, Subscribe>(
		subscribe,
		input => api.createSettlementNoThrow(input),
		config,
		...mutations
	)
}

export const getListBusinessCardsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListBusinessCards>) => ({
	queryKey: [Endpoint.ListBusinessCards, createStableQueryKey(query)],
	queryFn: () => api.listBusinessCards({ query })
})

export const preloadListBusinessCards = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListBusinessCards>
) => {
	return queryClient.ensureQueryData(getListBusinessCardsOptions(api, input))
}

export const useListBusinessCards = (
	query: EndpointQuery<Endpoint.ListBusinessCards>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListBusinessCards>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListBusinessCardsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListBusinessCards = (query: EndpointQuery<Endpoint.ListBusinessCards>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListBusinessCards, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listBusinessCards({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const getGetBusinessCardOptions = (api: Api, { path }: EndpointInput<Endpoint.GetBusinessCard>) => ({
	queryKey: [Endpoint.GetBusinessCard, createStableQueryKey(path)],
	queryFn: () => api.getBusinessCard({ path })
})

export const preloadGetBusinessCard = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetBusinessCard>
) => {
	return queryClient.ensureQueryData(getGetBusinessCardOptions(api, input))
}

export const useGetBusinessCard = (
	path: EndpointPath<Endpoint.GetBusinessCard>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetBusinessCard>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetBusinessCardOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.cardUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateBusinessCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateBusinessCard>,
	...mutations: Mutation<Endpoint.UpdateBusinessCard, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateBusinessCard, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateBusinessCard, Subscribe>(
		subscribe,
		input => api.updateBusinessCardNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteBusinessCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteBusinessCard>,
	...mutations: Mutation<Endpoint.DeleteBusinessCard, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteBusinessCard, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteBusinessCard, Subscribe>(
		subscribe,
		input => api.deleteBusinessCardNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseResetBusinessCardPin = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ResetBusinessCardPin>,
	...mutations: Mutation<Endpoint.ResetBusinessCardPin, any, any>[]
): EnhancedMutationApi<Endpoint.ResetBusinessCardPin, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ResetBusinessCardPin, Subscribe>(
		subscribe,
		input => api.resetBusinessCardPinNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseVerifyBusinessCardPin = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.VerifyBusinessCardPin>,
	...mutations: Mutation<Endpoint.VerifyBusinessCardPin, any, any>[]
): EnhancedMutationApi<Endpoint.VerifyBusinessCardPin, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.VerifyBusinessCardPin, Subscribe>(
		subscribe,
		input => api.verifyBusinessCardPinNoThrow(input),
		config,
		...mutations
	)
}

export const getGetBusinessCardTransactionsOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.GetBusinessCardTransactions>
) => ({
	queryKey: [Endpoint.GetBusinessCardTransactions, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getBusinessCardTransactions({ path, query })
})

export const preloadGetBusinessCardTransactions = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetBusinessCardTransactions>
) => {
	return queryClient.ensureQueryData(getGetBusinessCardTransactionsOptions(api, input))
}

export const useGetBusinessCardTransactions = (
	path: EndpointPath<Endpoint.GetBusinessCardTransactions>,
	query: EndpointQuery<Endpoint.GetBusinessCardTransactions>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetBusinessCardTransactions>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetBusinessCardTransactionsOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.cardUuid && (config?.enabled ?? true)
	})
}

export const usePaginatedGetBusinessCardTransactions = (
	path: EndpointPath<Endpoint.GetBusinessCardTransactions>,
	query: EndpointQuery<Endpoint.GetBusinessCardTransactions>
) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.GetBusinessCardTransactions, createStableQueryKey(path), createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) =>
			api.getBusinessCardTransactions({ path, query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const getGetSettlementOptions = (api: Api, { path }: EndpointInput<Endpoint.GetSettlement>) => ({
	queryKey: [Endpoint.GetSettlement, createStableQueryKey(path)],
	queryFn: () => api.getSettlement({ path })
})

export const preloadGetSettlement = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetSettlement>
) => {
	return queryClient.ensureQueryData(getGetSettlementOptions(api, input))
}

export const useGetSettlement = (
	path: EndpointPath<Endpoint.GetSettlement>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetSettlement>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetSettlementOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.settlementUuid && (config?.enabled ?? true)
	})
}

export const useBaseMarkSettlementSettled = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.MarkSettlementSettled>,
	...mutations: Mutation<Endpoint.MarkSettlementSettled, any, any>[]
): EnhancedMutationApi<Endpoint.MarkSettlementSettled, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.MarkSettlementSettled, Subscribe>(
		subscribe,
		input => api.markSettlementSettledNoThrow(input),
		config,
		...mutations
	)
}

export const getGetAnalyticsOptions = (api: Api, { query }: EndpointInput<Endpoint.GetAnalytics>) => ({
	queryKey: [Endpoint.GetAnalytics, createStableQueryKey(query)],
	queryFn: () => api.getAnalytics({ query })
})

export const preloadGetAnalytics = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetAnalytics>
) => {
	return queryClient.ensureQueryData(getGetAnalyticsOptions(api, input))
}

export const useGetAnalytics = (
	query: EndpointQuery<Endpoint.GetAnalytics>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetAnalytics>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetAnalyticsOptions(api, { query }),
		...(config ?? {}),
		enabled: !!query.interval && (config?.enabled ?? true)
	})
}

export const getGetBasketAnalysisOptions = (api: Api, { query }: EndpointInput<Endpoint.GetBasketAnalysis>) => ({
	queryKey: [Endpoint.GetBasketAnalysis, createStableQueryKey(query)],
	queryFn: () => api.getBasketAnalysis({ query })
})

export const preloadGetBasketAnalysis = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetBasketAnalysis>
) => {
	return queryClient.ensureQueryData(getGetBasketAnalysisOptions(api, input))
}

export const useGetBasketAnalysis = (
	query: EndpointQuery<Endpoint.GetBasketAnalysis>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetBasketAnalysis>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetBasketAnalysisOptions(api, { query }),
		...(config ?? {}),
		enabled: !!query.interval && (config?.enabled ?? true)
	})
}

export const getGetUserAnalyticsOptions = (api: Api, { query }: EndpointInput<Endpoint.GetUserAnalytics>) => ({
	queryKey: [Endpoint.GetUserAnalytics, createStableQueryKey(query)],
	queryFn: () => api.getUserAnalytics({ query })
})

export const preloadGetUserAnalytics = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetUserAnalytics>
) => {
	return queryClient.ensureQueryData(getGetUserAnalyticsOptions(api, input))
}

export const useGetUserAnalytics = (
	query: EndpointQuery<Endpoint.GetUserAnalytics>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetUserAnalytics>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetUserAnalyticsOptions(api, { query }),
		...(config ?? {}),
		enabled: !!query.interval && (config?.enabled ?? true)
	})
}

export const getGetRevenueReportOptions = (api: Api, { query }: EndpointInput<Endpoint.GetRevenueReport>) => ({
	queryKey: [Endpoint.GetRevenueReport, createStableQueryKey(query)],
	queryFn: () => api.getRevenueReport({ query })
})

export const preloadGetRevenueReport = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetRevenueReport>
) => {
	return queryClient.ensureQueryData(getGetRevenueReportOptions(api, input))
}

export const useGetRevenueReport = (
	query: EndpointQuery<Endpoint.GetRevenueReport>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetRevenueReport>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetRevenueReportOptions(api, { query }),
		...(config ?? {}),
		enabled: !!query.interval && (config?.enabled ?? true)
	})
}

export const getGetProductReportOptions = (api: Api, { query }: EndpointInput<Endpoint.GetProductReport>) => ({
	queryKey: [Endpoint.GetProductReport, createStableQueryKey(query)],
	queryFn: () => api.getProductReport({ query })
})

export const preloadGetProductReport = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetProductReport>
) => {
	return queryClient.ensureQueryData(getGetProductReportOptions(api, input))
}

export const useGetProductReport = (
	query: EndpointQuery<Endpoint.GetProductReport>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetProductReport>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetProductReportOptions(api, { query }),
		...(config ?? {}),
		enabled: !!query.interval && (config?.enabled ?? true)
	})
}

export const getListCustomersOptions = (api: Api, { query }: EndpointInput<Endpoint.ListCustomers>) => ({
	queryKey: [Endpoint.ListCustomers, createStableQueryKey(query)],
	queryFn: () => api.listCustomers({ query })
})

export const preloadListCustomers = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListCustomers>
) => {
	return queryClient.ensureQueryData(getListCustomersOptions(api, input))
}

export const useListCustomers = (
	query: EndpointQuery<Endpoint.ListCustomers>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListCustomers>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListCustomersOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListCustomers = (query: EndpointQuery<Endpoint.ListCustomers>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListCustomers, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listCustomers({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateCustomer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateCustomer>,
	...mutations: Mutation<Endpoint.CreateCustomer, any, any>[]
): EnhancedMutationApi<Endpoint.CreateCustomer, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateCustomer, Subscribe>(
		subscribe,
		input => api.createCustomerNoThrow(input),
		config,
		...mutations
	)
}

export const getGetCustomerOptions = (api: Api, { path }: EndpointInput<Endpoint.GetCustomer>) => ({
	queryKey: [Endpoint.GetCustomer, createStableQueryKey(path)],
	queryFn: () => api.getCustomer({ path })
})

export const preloadGetCustomer = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetCustomer>
) => {
	return queryClient.ensureQueryData(getGetCustomerOptions(api, input))
}

export const useGetCustomer = (
	path: EndpointPath<Endpoint.GetCustomer>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetCustomer>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetCustomerOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.customerUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateCustomer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateCustomer>,
	...mutations: Mutation<Endpoint.UpdateCustomer, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateCustomer, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateCustomer, Subscribe>(
		subscribe,
		input => api.updateCustomerNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseRevokeCustomerTokens = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RevokeCustomerTokens>,
	...mutations: Mutation<Endpoint.RevokeCustomerTokens, any, any>[]
): EnhancedMutationApi<Endpoint.RevokeCustomerTokens, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RevokeCustomerTokens, Subscribe>(
		subscribe,
		input => api.revokeCustomerTokensNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseAssumeCustomerToken = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.AssumeCustomerToken>,
	...mutations: Mutation<Endpoint.AssumeCustomerToken, any, any>[]
): EnhancedMutationApi<Endpoint.AssumeCustomerToken, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.AssumeCustomerToken, Subscribe>(
		subscribe,
		input => api.assumeCustomerTokenNoThrow(input),
		config,
		...mutations
	)
}

export const getListCustomerGroupsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListCustomerGroups>) => ({
	queryKey: [Endpoint.ListCustomerGroups, createStableQueryKey(query)],
	queryFn: () => api.listCustomerGroups({ query })
})

export const preloadListCustomerGroups = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListCustomerGroups>
) => {
	return queryClient.ensureQueryData(getListCustomerGroupsOptions(api, input))
}

export const useListCustomerGroups = (
	query: EndpointQuery<Endpoint.ListCustomerGroups>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListCustomerGroups>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListCustomerGroupsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListCustomerGroups = (query: EndpointQuery<Endpoint.ListCustomerGroups>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListCustomerGroups, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listCustomerGroups({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateCustomerGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateCustomerGroup>,
	...mutations: Mutation<Endpoint.CreateCustomerGroup, any, any>[]
): EnhancedMutationApi<Endpoint.CreateCustomerGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateCustomerGroup, Subscribe>(
		subscribe,
		input => api.createCustomerGroupNoThrow(input),
		config,
		...mutations
	)
}

export const getGetCustomerGroupOptions = (api: Api, { path }: EndpointInput<Endpoint.GetCustomerGroup>) => ({
	queryKey: [Endpoint.GetCustomerGroup, createStableQueryKey(path)],
	queryFn: () => api.getCustomerGroup({ path })
})

export const preloadGetCustomerGroup = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetCustomerGroup>
) => {
	return queryClient.ensureQueryData(getGetCustomerGroupOptions(api, input))
}

export const useGetCustomerGroup = (
	path: EndpointPath<Endpoint.GetCustomerGroup>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetCustomerGroup>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetCustomerGroupOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.customerGroupUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateCustomerGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateCustomerGroup>,
	...mutations: Mutation<Endpoint.UpdateCustomerGroup, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateCustomerGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateCustomerGroup, Subscribe>(
		subscribe,
		input => api.updateCustomerGroupNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteCustomerGroup = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteCustomerGroup>,
	...mutations: Mutation<Endpoint.DeleteCustomerGroup, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteCustomerGroup, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteCustomerGroup, Subscribe>(
		subscribe,
		input => api.deleteCustomerGroupNoThrow(input),
		config,
		...mutations
	)
}

export const getListLoyaltyCardsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListLoyaltyCards>) => ({
	queryKey: [Endpoint.ListLoyaltyCards, createStableQueryKey(query)],
	queryFn: () => api.listLoyaltyCards({ query })
})

export const preloadListLoyaltyCards = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListLoyaltyCards>
) => {
	return queryClient.ensureQueryData(getListLoyaltyCardsOptions(api, input))
}

export const useListLoyaltyCards = (
	query: EndpointQuery<Endpoint.ListLoyaltyCards>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListLoyaltyCards>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListLoyaltyCardsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListLoyaltyCards = (query: EndpointQuery<Endpoint.ListLoyaltyCards>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListLoyaltyCards, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listLoyaltyCards({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateLoyaltyCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateLoyaltyCard>,
	...mutations: Mutation<Endpoint.CreateLoyaltyCard, any, any>[]
): EnhancedMutationApi<Endpoint.CreateLoyaltyCard, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateLoyaltyCard, Subscribe>(
		subscribe,
		input => api.createLoyaltyCardNoThrow(input),
		config,
		...mutations
	)
}

export const getGetLoyaltyCardOptions = (api: Api, { path }: EndpointInput<Endpoint.GetLoyaltyCard>) => ({
	queryKey: [Endpoint.GetLoyaltyCard, createStableQueryKey(path)],
	queryFn: () => api.getLoyaltyCard({ path })
})

export const preloadGetLoyaltyCard = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetLoyaltyCard>
) => {
	return queryClient.ensureQueryData(getGetLoyaltyCardOptions(api, input))
}

export const useGetLoyaltyCard = (
	path: EndpointPath<Endpoint.GetLoyaltyCard>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetLoyaltyCard>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetLoyaltyCardOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.cardUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateLoyaltyCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateLoyaltyCard>,
	...mutations: Mutation<Endpoint.UpdateLoyaltyCard, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateLoyaltyCard, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateLoyaltyCard, Subscribe>(
		subscribe,
		input => api.updateLoyaltyCardNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteLoyaltyCard = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteLoyaltyCard>,
	...mutations: Mutation<Endpoint.DeleteLoyaltyCard, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteLoyaltyCard, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteLoyaltyCard, Subscribe>(
		subscribe,
		input => api.deleteLoyaltyCardNoThrow(input),
		config,
		...mutations
	)
}

export const getGetLoyaltyCardRedemptionsOptions = (
	api: Api,
	{ path, query }: EndpointInput<Endpoint.GetLoyaltyCardRedemptions>
) => ({
	queryKey: [Endpoint.GetLoyaltyCardRedemptions, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getLoyaltyCardRedemptions({ path, query })
})

export const preloadGetLoyaltyCardRedemptions = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetLoyaltyCardRedemptions>
) => {
	return queryClient.ensureQueryData(getGetLoyaltyCardRedemptionsOptions(api, input))
}

export const useGetLoyaltyCardRedemptions = (
	path: EndpointPath<Endpoint.GetLoyaltyCardRedemptions>,
	query: EndpointQuery<Endpoint.GetLoyaltyCardRedemptions>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetLoyaltyCardRedemptions>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetLoyaltyCardRedemptionsOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.cardUuid && (config?.enabled ?? true)
	})
}

export const usePaginatedGetLoyaltyCardRedemptions = (
	path: EndpointPath<Endpoint.GetLoyaltyCardRedemptions>,
	query: EndpointQuery<Endpoint.GetLoyaltyCardRedemptions>
) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.GetLoyaltyCardRedemptions, createStableQueryKey(path), createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.getLoyaltyCardRedemptions({ path, query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const getGetDashboardFeaturesOptions = (api: Api, {}: EndpointInput<Endpoint.GetDashboardFeatures>) => ({
	queryKey: [Endpoint.GetDashboardFeatures],
	queryFn: () => api.getDashboardFeatures({})
})

export const preloadGetDashboardFeatures = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetDashboardFeatures>
) => {
	return queryClient.ensureQueryData(getGetDashboardFeaturesOptions(api, input))
}

export const useGetDashboardFeatures = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetDashboardFeatures>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetDashboardFeaturesOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseSubmitDeveloperFeedback = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SubmitDeveloperFeedback>,
	...mutations: Mutation<Endpoint.SubmitDeveloperFeedback, any, any>[]
): EnhancedMutationApi<Endpoint.SubmitDeveloperFeedback, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SubmitDeveloperFeedback, Subscribe>(
		subscribe,
		input => api.submitDeveloperFeedbackNoThrow(input),
		config,
		...mutations
	)
}

export const getListInventoryLocationsOptions = (
	api: Api,
	{ query }: EndpointInput<Endpoint.ListInventoryLocations>
) => ({
	queryKey: [Endpoint.ListInventoryLocations, createStableQueryKey(query)],
	queryFn: () => api.listInventoryLocations({ query })
})

export const preloadListInventoryLocations = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListInventoryLocations>
) => {
	return queryClient.ensureQueryData(getListInventoryLocationsOptions(api, input))
}

export const useListInventoryLocations = (
	query: EndpointQuery<Endpoint.ListInventoryLocations>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListInventoryLocations>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListInventoryLocationsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListInventoryLocations = (query: EndpointQuery<Endpoint.ListInventoryLocations>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListInventoryLocations, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listInventoryLocations({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateInventoryLocation = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateInventoryLocation>,
	...mutations: Mutation<Endpoint.CreateInventoryLocation, any, any>[]
): EnhancedMutationApi<Endpoint.CreateInventoryLocation, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateInventoryLocation, Subscribe>(
		subscribe,
		input => api.createInventoryLocationNoThrow(input),
		config,
		...mutations
	)
}

export const getSearchInventoryVariantsOptions = (
	api: Api,
	{ query }: EndpointInput<Endpoint.SearchInventoryVariants>
) => ({
	queryKey: [Endpoint.SearchInventoryVariants, createStableQueryKey(query)],
	queryFn: () => api.searchInventoryVariants({ query })
})

export const preloadSearchInventoryVariants = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.SearchInventoryVariants>
) => {
	return queryClient.ensureQueryData(getSearchInventoryVariantsOptions(api, input))
}

export const useSearchInventoryVariants = (
	query: EndpointQuery<Endpoint.SearchInventoryVariants>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.SearchInventoryVariants>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getSearchInventoryVariantsOptions(api, { query }),
		...(config ?? {}),
		enabled: !!query.q && (config?.enabled ?? true)
	})
}

export const getGetInventoryLocationOptions = (api: Api, { path }: EndpointInput<Endpoint.GetInventoryLocation>) => ({
	queryKey: [Endpoint.GetInventoryLocation, createStableQueryKey(path)],
	queryFn: () => api.getInventoryLocation({ path })
})

export const preloadGetInventoryLocation = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetInventoryLocation>
) => {
	return queryClient.ensureQueryData(getGetInventoryLocationOptions(api, input))
}

export const useGetInventoryLocation = (
	path: EndpointPath<Endpoint.GetInventoryLocation>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetInventoryLocation>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetInventoryLocationOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.inventoryLocationUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateInventoryLocation = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateInventoryLocation>,
	...mutations: Mutation<Endpoint.UpdateInventoryLocation, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateInventoryLocation, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateInventoryLocation, Subscribe>(
		subscribe,
		input => api.updateInventoryLocationNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseProvisionInventoryLocationLevels = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ProvisionInventoryLocationLevels>,
	...mutations: Mutation<Endpoint.ProvisionInventoryLocationLevels, any, any>[]
): EnhancedMutationApi<Endpoint.ProvisionInventoryLocationLevels, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ProvisionInventoryLocationLevels, Subscribe>(
		subscribe,
		input => api.provisionInventoryLocationLevelsNoThrow(input),
		config,
		...mutations
	)
}

export const getGetInventorySettingsOptions = (api: Api, {}: EndpointInput<Endpoint.GetInventorySettings>) => ({
	queryKey: [Endpoint.GetInventorySettings],
	queryFn: () => api.getInventorySettings({})
})

export const preloadGetInventorySettings = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetInventorySettings>
) => {
	return queryClient.ensureQueryData(getGetInventorySettingsOptions(api, input))
}

export const useGetInventorySettings = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetInventorySettings>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetInventorySettingsOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseUpdateInventorySettings = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateInventorySettings>,
	...mutations: Mutation<Endpoint.UpdateInventorySettings, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateInventorySettings, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateInventorySettings, Subscribe>(
		subscribe,
		input => api.updateInventorySettingsNoThrow(input),
		config,
		...mutations
	)
}

export const getListInventoryLevelsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListInventoryLevels>) => ({
	queryKey: [Endpoint.ListInventoryLevels, createStableQueryKey(query)],
	queryFn: () => api.listInventoryLevels({ query })
})

export const preloadListInventoryLevels = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListInventoryLevels>
) => {
	return queryClient.ensureQueryData(getListInventoryLevelsOptions(api, input))
}

export const useListInventoryLevels = (
	query: EndpointQuery<Endpoint.ListInventoryLevels>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListInventoryLevels>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListInventoryLevelsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListInventoryLevels = (query: EndpointQuery<Endpoint.ListInventoryLevels>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListInventoryLevels, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listInventoryLevels({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const getGetInventorySummaryOptions = (api: Api, { query }: EndpointInput<Endpoint.GetInventorySummary>) => ({
	queryKey: [Endpoint.GetInventorySummary, createStableQueryKey(query)],
	queryFn: () => api.getInventorySummary({ query })
})

export const preloadGetInventorySummary = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetInventorySummary>
) => {
	return queryClient.ensureQueryData(getGetInventorySummaryOptions(api, input))
}

export const useGetInventorySummary = (
	query: EndpointQuery<Endpoint.GetInventorySummary>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetInventorySummary>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetInventorySummaryOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetInventoryLevelOptions = (api: Api, { path }: EndpointInput<Endpoint.GetInventoryLevel>) => ({
	queryKey: [Endpoint.GetInventoryLevel, createStableQueryKey(path)],
	queryFn: () => api.getInventoryLevel({ path })
})

export const preloadGetInventoryLevel = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetInventoryLevel>
) => {
	return queryClient.ensureQueryData(getGetInventoryLevelOptions(api, input))
}

export const useGetInventoryLevel = (
	path: EndpointPath<Endpoint.GetInventoryLevel>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetInventoryLevel>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetInventoryLevelOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.inventoryLevelUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateInventoryLevel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateInventoryLevel>,
	...mutations: Mutation<Endpoint.UpdateInventoryLevel, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateInventoryLevel, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateInventoryLevel, Subscribe>(
		subscribe,
		input => api.updateInventoryLevelNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseAdjustInventoryLevel = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.AdjustInventoryLevel>,
	...mutations: Mutation<Endpoint.AdjustInventoryLevel, any, any>[]
): EnhancedMutationApi<Endpoint.AdjustInventoryLevel, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.AdjustInventoryLevel, Subscribe>(
		subscribe,
		input => api.adjustInventoryLevelNoThrow(input),
		config,
		...mutations
	)
}

export const getListStockMovementsOptions = (api: Api, { query }: EndpointInput<Endpoint.ListStockMovements>) => ({
	queryKey: [Endpoint.ListStockMovements, createStableQueryKey(query)],
	queryFn: () => api.listStockMovements({ query })
})

export const preloadListStockMovements = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListStockMovements>
) => {
	return queryClient.ensureQueryData(getListStockMovementsOptions(api, input))
}

export const useListStockMovements = (
	query: EndpointQuery<Endpoint.ListStockMovements>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListStockMovements>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListStockMovementsOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListStockMovements = (query: EndpointQuery<Endpoint.ListStockMovements>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListStockMovements, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listStockMovements({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const getListSuppliersOptions = (api: Api, { query }: EndpointInput<Endpoint.ListSuppliers>) => ({
	queryKey: [Endpoint.ListSuppliers, createStableQueryKey(query)],
	queryFn: () => api.listSuppliers({ query })
})

export const preloadListSuppliers = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListSuppliers>
) => {
	return queryClient.ensureQueryData(getListSuppliersOptions(api, input))
}

export const useListSuppliers = (
	query: EndpointQuery<Endpoint.ListSuppliers>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListSuppliers>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListSuppliersOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListSuppliers = (query: EndpointQuery<Endpoint.ListSuppliers>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListSuppliers, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listSuppliers({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateSupplier = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateSupplier>,
	...mutations: Mutation<Endpoint.CreateSupplier, any, any>[]
): EnhancedMutationApi<Endpoint.CreateSupplier, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateSupplier, Subscribe>(
		subscribe,
		input => api.createSupplierNoThrow(input),
		config,
		...mutations
	)
}

export const getGetSupplierOptions = (api: Api, { path }: EndpointInput<Endpoint.GetSupplier>) => ({
	queryKey: [Endpoint.GetSupplier, createStableQueryKey(path)],
	queryFn: () => api.getSupplier({ path })
})

export const preloadGetSupplier = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetSupplier>
) => {
	return queryClient.ensureQueryData(getGetSupplierOptions(api, input))
}

export const useGetSupplier = (
	path: EndpointPath<Endpoint.GetSupplier>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetSupplier>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetSupplierOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.supplierUuid && (config?.enabled ?? true)
	})
}

export const useBaseUpdateSupplier = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateSupplier>,
	...mutations: Mutation<Endpoint.UpdateSupplier, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateSupplier, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateSupplier, Subscribe>(
		subscribe,
		input => api.updateSupplierNoThrow(input),
		config,
		...mutations
	)
}

export const getListPurchaseOrdersOptions = (api: Api, { query }: EndpointInput<Endpoint.ListPurchaseOrders>) => ({
	queryKey: [Endpoint.ListPurchaseOrders, createStableQueryKey(query)],
	queryFn: () => api.listPurchaseOrders({ query })
})

export const preloadListPurchaseOrders = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListPurchaseOrders>
) => {
	return queryClient.ensureQueryData(getListPurchaseOrdersOptions(api, input))
}

export const useListPurchaseOrders = (
	query: EndpointQuery<Endpoint.ListPurchaseOrders>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListPurchaseOrders>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListPurchaseOrdersOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListPurchaseOrders = (query: EndpointQuery<Endpoint.ListPurchaseOrders>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListPurchaseOrders, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listPurchaseOrders({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreatePurchaseOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreatePurchaseOrder>,
	...mutations: Mutation<Endpoint.CreatePurchaseOrder, any, any>[]
): EnhancedMutationApi<Endpoint.CreatePurchaseOrder, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreatePurchaseOrder, Subscribe>(
		subscribe,
		input => api.createPurchaseOrderNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCreateReplenishmentPurchaseOrders = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateReplenishmentPurchaseOrders>,
	...mutations: Mutation<Endpoint.CreateReplenishmentPurchaseOrders, any, any>[]
): EnhancedMutationApi<Endpoint.CreateReplenishmentPurchaseOrders, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateReplenishmentPurchaseOrders, Subscribe>(
		subscribe,
		input => api.createReplenishmentPurchaseOrdersNoThrow(input),
		config,
		...mutations
	)
}

export const getGetPurchaseOrderOptions = (api: Api, { path }: EndpointInput<Endpoint.GetPurchaseOrder>) => ({
	queryKey: [Endpoint.GetPurchaseOrder, createStableQueryKey(path)],
	queryFn: () => api.getPurchaseOrder({ path })
})

export const preloadGetPurchaseOrder = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetPurchaseOrder>
) => {
	return queryClient.ensureQueryData(getGetPurchaseOrderOptions(api, input))
}

export const useGetPurchaseOrder = (
	path: EndpointPath<Endpoint.GetPurchaseOrder>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetPurchaseOrder>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetPurchaseOrderOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.purchaseOrderUuid && (config?.enabled ?? true)
	})
}

export const useBaseReceivePurchaseOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ReceivePurchaseOrder>,
	...mutations: Mutation<Endpoint.ReceivePurchaseOrder, any, any>[]
): EnhancedMutationApi<Endpoint.ReceivePurchaseOrder, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ReceivePurchaseOrder, Subscribe>(
		subscribe,
		input => api.receivePurchaseOrderNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseMarkPurchaseOrderOrdered = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.MarkPurchaseOrderOrdered>,
	...mutations: Mutation<Endpoint.MarkPurchaseOrderOrdered, any, any>[]
): EnhancedMutationApi<Endpoint.MarkPurchaseOrderOrdered, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.MarkPurchaseOrderOrdered, Subscribe>(
		subscribe,
		input => api.markPurchaseOrderOrderedNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCancelPurchaseOrder = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CancelPurchaseOrder>,
	...mutations: Mutation<Endpoint.CancelPurchaseOrder, any, any>[]
): EnhancedMutationApi<Endpoint.CancelPurchaseOrder, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CancelPurchaseOrder, Subscribe>(
		subscribe,
		input => api.cancelPurchaseOrderNoThrow(input),
		config,
		...mutations
	)
}

export const getListInventoryTransfersOptions = (
	api: Api,
	{ query }: EndpointInput<Endpoint.ListInventoryTransfers>
) => ({
	queryKey: [Endpoint.ListInventoryTransfers, createStableQueryKey(query)],
	queryFn: () => api.listInventoryTransfers({ query })
})

export const preloadListInventoryTransfers = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListInventoryTransfers>
) => {
	return queryClient.ensureQueryData(getListInventoryTransfersOptions(api, input))
}

export const useListInventoryTransfers = (
	query: EndpointQuery<Endpoint.ListInventoryTransfers>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListInventoryTransfers>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListInventoryTransfersOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListInventoryTransfers = (query: EndpointQuery<Endpoint.ListInventoryTransfers>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListInventoryTransfers, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listInventoryTransfers({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseCreateInventoryTransfer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateInventoryTransfer>,
	...mutations: Mutation<Endpoint.CreateInventoryTransfer, any, any>[]
): EnhancedMutationApi<Endpoint.CreateInventoryTransfer, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateInventoryTransfer, Subscribe>(
		subscribe,
		input => api.createInventoryTransferNoThrow(input),
		config,
		...mutations
	)
}

export const getGetInventoryTransferOptions = (api: Api, { path }: EndpointInput<Endpoint.GetInventoryTransfer>) => ({
	queryKey: [Endpoint.GetInventoryTransfer, createStableQueryKey(path)],
	queryFn: () => api.getInventoryTransfer({ path })
})

export const preloadGetInventoryTransfer = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetInventoryTransfer>
) => {
	return queryClient.ensureQueryData(getGetInventoryTransferOptions(api, input))
}

export const useGetInventoryTransfer = (
	path: EndpointPath<Endpoint.GetInventoryTransfer>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetInventoryTransfer>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetInventoryTransferOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.inventoryTransferUuid && (config?.enabled ?? true)
	})
}

export const useBaseShipInventoryTransfer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ShipInventoryTransfer>,
	...mutations: Mutation<Endpoint.ShipInventoryTransfer, any, any>[]
): EnhancedMutationApi<Endpoint.ShipInventoryTransfer, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ShipInventoryTransfer, Subscribe>(
		subscribe,
		input => api.shipInventoryTransferNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseReceiveInventoryTransfer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.ReceiveInventoryTransfer>,
	...mutations: Mutation<Endpoint.ReceiveInventoryTransfer, any, any>[]
): EnhancedMutationApi<Endpoint.ReceiveInventoryTransfer, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.ReceiveInventoryTransfer, Subscribe>(
		subscribe,
		input => api.receiveInventoryTransferNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCancelInventoryTransfer = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CancelInventoryTransfer>,
	...mutations: Mutation<Endpoint.CancelInventoryTransfer, any, any>[]
): EnhancedMutationApi<Endpoint.CancelInventoryTransfer, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CancelInventoryTransfer, Subscribe>(
		subscribe,
		input => api.cancelInventoryTransferNoThrow(input),
		config,
		...mutations
	)
}

export const getGetInventoryReportSummaryOptions = (
	api: Api,
	{ query }: EndpointInput<Endpoint.GetInventoryReportSummary>
) => ({
	queryKey: [Endpoint.GetInventoryReportSummary, createStableQueryKey(query)],
	queryFn: () => api.getInventoryReportSummary({ query })
})

export const preloadGetInventoryReportSummary = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetInventoryReportSummary>
) => {
	return queryClient.ensureQueryData(getGetInventoryReportSummaryOptions(api, input))
}

export const useGetInventoryReportSummary = (
	query: EndpointQuery<Endpoint.GetInventoryReportSummary>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetInventoryReportSummary>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetInventoryReportSummaryOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getListVariantSuppliersOptions = (api: Api, { query }: EndpointInput<Endpoint.ListVariantSuppliers>) => ({
	queryKey: [Endpoint.ListVariantSuppliers, createStableQueryKey(query)],
	queryFn: () => api.listVariantSuppliers({ query })
})

export const preloadListVariantSuppliers = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListVariantSuppliers>
) => {
	return queryClient.ensureQueryData(getListVariantSuppliersOptions(api, input))
}

export const useListVariantSuppliers = (
	query: EndpointQuery<Endpoint.ListVariantSuppliers>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListVariantSuppliers>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListVariantSuppliersOptions(api, { query }),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const usePaginatedListVariantSuppliers = (query: EndpointQuery<Endpoint.ListVariantSuppliers>) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.ListVariantSuppliers, createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.listVariantSuppliers({ query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const useBaseUpsertVariantSupplier = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpsertVariantSupplier>,
	...mutations: Mutation<Endpoint.UpsertVariantSupplier, any, any>[]
): EnhancedMutationApi<Endpoint.UpsertVariantSupplier, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpsertVariantSupplier, Subscribe>(
		subscribe,
		input => api.upsertVariantSupplierNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseSetPreferredVariantSupplier = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.SetPreferredVariantSupplier>,
	...mutations: Mutation<Endpoint.SetPreferredVariantSupplier, any, any>[]
): EnhancedMutationApi<Endpoint.SetPreferredVariantSupplier, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.SetPreferredVariantSupplier, Subscribe>(
		subscribe,
		input => api.setPreferredVariantSupplierNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteVariantSupplier = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteVariantSupplier>,
	...mutations: Mutation<Endpoint.DeleteVariantSupplier, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteVariantSupplier, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteVariantSupplier, Subscribe>(
		subscribe,
		input => api.deleteVariantSupplierNoThrow(input),
		config,
		...mutations
	)
}

export const getListIntegrationsOptions = (api: Api, {}: EndpointInput<Endpoint.ListIntegrations>) => ({
	queryKey: [Endpoint.ListIntegrations],
	queryFn: () => api.listIntegrations({})
})

export const preloadListIntegrations = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListIntegrations>
) => {
	return queryClient.ensureQueryData(getListIntegrationsOptions(api, input))
}

export const useListIntegrations = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListIntegrations>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getListIntegrationsOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetTeyaIntegrationOptions = (api: Api, {}: EndpointInput<Endpoint.GetTeyaIntegration>) => ({
	queryKey: [Endpoint.GetTeyaIntegration],
	queryFn: () => api.getTeyaIntegration({})
})

export const preloadGetTeyaIntegration = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetTeyaIntegration>
) => {
	return queryClient.ensureQueryData(getGetTeyaIntegrationOptions(api, input))
}

export const useGetTeyaIntegration = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetTeyaIntegration>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetTeyaIntegrationOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseCreateTeyaConnectUrl = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateTeyaConnectUrl>,
	...mutations: Mutation<Endpoint.CreateTeyaConnectUrl, any, any>[]
): EnhancedMutationApi<Endpoint.CreateTeyaConnectUrl, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateTeyaConnectUrl, Subscribe>(
		subscribe,
		input => api.createTeyaConnectUrlNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDisconnectTeya = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DisconnectTeya>,
	...mutations: Mutation<Endpoint.DisconnectTeya, any, any>[]
): EnhancedMutationApi<Endpoint.DisconnectTeya, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DisconnectTeya, Subscribe>(
		subscribe,
		input => api.disconnectTeyaNoThrow(input),
		config,
		...mutations
	)
}

export const getListTeyaStoresOptions = (api: Api, {}: EndpointInput<Endpoint.ListTeyaStores>) => ({
	queryKey: [Endpoint.ListTeyaStores],
	queryFn: () => api.listTeyaStores({})
})

export const preloadListTeyaStores = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListTeyaStores>
) => {
	return queryClient.ensureQueryData(getListTeyaStoresOptions(api, input))
}

export const useListTeyaStores = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListTeyaStores>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getListTeyaStoresOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getListTeyaTerminalsOptions = (api: Api, { path }: EndpointInput<Endpoint.ListTeyaTerminals>) => ({
	queryKey: [Endpoint.ListTeyaTerminals, createStableQueryKey(path)],
	queryFn: () => api.listTeyaTerminals({ path })
})

export const preloadListTeyaTerminals = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.ListTeyaTerminals>
) => {
	return queryClient.ensureQueryData(getListTeyaTerminalsOptions(api, input))
}

export const useListTeyaTerminals = (
	path: EndpointPath<Endpoint.ListTeyaTerminals>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.ListTeyaTerminals>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getListTeyaTerminalsOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.storeId && (config?.enabled ?? true)
	})
}

export const useBaseRegisterRapydDevice = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.RegisterRapydDevice>,
	...mutations: Mutation<Endpoint.RegisterRapydDevice, any, any>[]
): EnhancedMutationApi<Endpoint.RegisterRapydDevice, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.RegisterRapydDevice, Subscribe>(
		subscribe,
		input => api.registerRapydDeviceNoThrow(input),
		config,
		...mutations
	)
}

export const getPollRapydDeviceTokenOptions = (api: Api, { path }: EndpointInput<Endpoint.PollRapydDeviceToken>) => ({
	queryKey: [Endpoint.PollRapydDeviceToken, createStableQueryKey(path)],
	queryFn: () => api.pollRapydDeviceToken({ path })
})

export const preloadPollRapydDeviceToken = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.PollRapydDeviceToken>
) => {
	return queryClient.ensureQueryData(getPollRapydDeviceTokenOptions(api, input))
}

export const usePollRapydDeviceToken = (
	path: EndpointPath<Endpoint.PollRapydDeviceToken>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.PollRapydDeviceToken>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getPollRapydDeviceTokenOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.serialNumber && (config?.enabled ?? true)
	})
}

export const getGetIntegrationConfigOptions = (api: Api, { path }: EndpointInput<Endpoint.GetIntegrationConfig>) => ({
	queryKey: [Endpoint.GetIntegrationConfig, createStableQueryKey(path)],
	queryFn: () => api.getIntegrationConfig({ path })
})

export const preloadGetIntegrationConfig = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetIntegrationConfig>
) => {
	return queryClient.ensureQueryData(getGetIntegrationConfigOptions(api, input))
}

export const useGetIntegrationConfig = (
	path: EndpointPath<Endpoint.GetIntegrationConfig>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetIntegrationConfig>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetIntegrationConfigOptions(api, { path }),
		...(config ?? {}),
		enabled: !!path.provider && (config?.enabled ?? true)
	})
}

export const useBasePutIntegrationGlobalConfig = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.PutIntegrationGlobalConfig>,
	...mutations: Mutation<Endpoint.PutIntegrationGlobalConfig, any, any>[]
): EnhancedMutationApi<Endpoint.PutIntegrationGlobalConfig, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.PutIntegrationGlobalConfig, Subscribe>(
		subscribe,
		input => api.putIntegrationGlobalConfigNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseCreateIntegrationOverride = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.CreateIntegrationOverride>,
	...mutations: Mutation<Endpoint.CreateIntegrationOverride, any, any>[]
): EnhancedMutationApi<Endpoint.CreateIntegrationOverride, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.CreateIntegrationOverride, Subscribe>(
		subscribe,
		input => api.createIntegrationOverrideNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseUpdateIntegrationOverride = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.UpdateIntegrationOverride>,
	...mutations: Mutation<Endpoint.UpdateIntegrationOverride, any, any>[]
): EnhancedMutationApi<Endpoint.UpdateIntegrationOverride, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.UpdateIntegrationOverride, Subscribe>(
		subscribe,
		input => api.updateIntegrationOverrideNoThrow(input),
		config,
		...mutations
	)
}

export const useBaseDeleteIntegrationOverride = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.DeleteIntegrationOverride>,
	...mutations: Mutation<Endpoint.DeleteIntegrationOverride, any, any>[]
): EnhancedMutationApi<Endpoint.DeleteIntegrationOverride, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.DeleteIntegrationOverride, Subscribe>(
		subscribe,
		input => api.deleteIntegrationOverrideNoThrow(input),
		config,
		...mutations
	)
}

export const getGetBillingPlanOptions = (api: Api, {}: EndpointInput<Endpoint.GetBillingPlan>) => ({
	queryKey: [Endpoint.GetBillingPlan],
	queryFn: () => api.getBillingPlan({})
})

export const preloadGetBillingPlan = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetBillingPlan>
) => {
	return queryClient.ensureQueryData(getGetBillingPlanOptions(api, input))
}

export const useGetBillingPlan = (config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetBillingPlan>>>) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetBillingPlanOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const useBaseAcceptBillingPlan = <Subscribe extends boolean = false>(
	subscribe: Subscribe = false as Subscribe,
	config?: MutationConfig<Endpoint.AcceptBillingPlan>,
	...mutations: Mutation<Endpoint.AcceptBillingPlan, any, any>[]
): EnhancedMutationApi<Endpoint.AcceptBillingPlan, Subscribe> => {
	const { api } = useApiContext()
	return useEnhancedMutationMaybe<Endpoint.AcceptBillingPlan, Subscribe>(
		subscribe,
		input => api.acceptBillingPlanNoThrow(input),
		config,
		...mutations
	)
}

export const getGetAppUpdateBranchesOptions = (api: Api, {}: EndpointInput<Endpoint.GetAppUpdateBranches>) => ({
	queryKey: [Endpoint.GetAppUpdateBranches],
	queryFn: () => api.getAppUpdateBranches({})
})

export const preloadGetAppUpdateBranches = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetAppUpdateBranches>
) => {
	return queryClient.ensureQueryData(getGetAppUpdateBranchesOptions(api, input))
}

export const useGetAppUpdateBranches = (
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetAppUpdateBranches>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetAppUpdateBranchesOptions(api, {}),
		...(config ?? {}),
		enabled: config?.enabled ?? true
	})
}

export const getGetAppUpdatesOptions = (api: Api, { path, query }: EndpointInput<Endpoint.GetAppUpdates>) => ({
	queryKey: [Endpoint.GetAppUpdates, createStableQueryKey(path), createStableQueryKey(query)],
	queryFn: () => api.getAppUpdates({ path, query })
})

export const preloadGetAppUpdates = (
	{ api, queryClient }: PreloadContext,
	input: EndpointInput<Endpoint.GetAppUpdates>
) => {
	return queryClient.ensureQueryData(getGetAppUpdatesOptions(api, input))
}

export const useGetAppUpdates = (
	path: EndpointPath<Endpoint.GetAppUpdates>,
	query: EndpointQuery<Endpoint.GetAppUpdates>,
	config?: Partial<UseQueryOptions<EndpointResponse<Endpoint.GetAppUpdates>>>
) => {
	const { api } = useApiContext()
	return useQuery({
		...getGetAppUpdatesOptions(api, { path, query }),
		...(config ?? {}),
		enabled: !!path.branchName && (config?.enabled ?? true)
	})
}

export const usePaginatedGetAppUpdates = (
	path: EndpointPath<Endpoint.GetAppUpdates>,
	query: EndpointQuery<Endpoint.GetAppUpdates>
) => {
	const { api } = useApiContext()
	return useInfiniteQuery({
		queryKey: [Endpoint.GetAppUpdates, createStableQueryKey(path), createStableQueryKey(query)],
		queryFn: ({ pageParam = 0 }) => api.getAppUpdates({ path, query: { ...query, offset: pageParam } }),
		initialPageParam: 0,
		getNextPageParam: lastPage => {
			const nextOffset = lastPage.offset + lastPage.limit
			return nextOffset >= lastPage.count ? undefined : nextOffset
		}
	})
}

export const QUERY_KEYS = {
	[Endpoint.GetApiKey]: ({}: EndpointInput<Endpoint.GetApiKey>) => [Endpoint.GetApiKey],
	[Endpoint.ListUsers]: ({}: EndpointInput<Endpoint.ListUsers>) => [Endpoint.ListUsers],
	[Endpoint.GetMe]: ({}: EndpointInput<Endpoint.GetMe>) => [Endpoint.GetMe],
	[Endpoint.GetUser]: ({ path }: EndpointInput<Endpoint.GetUser>) => [Endpoint.GetUser, createStableQueryKey(path)],
	[Endpoint.GetUserSignInCredentials]: ({ path }: EndpointInput<Endpoint.GetUserSignInCredentials>) => [
		Endpoint.GetUserSignInCredentials,
		createStableQueryKey(path)
	],
	[Endpoint.ListInvites]: ({}: EndpointInput<Endpoint.ListInvites>) => [Endpoint.ListInvites],
	[Endpoint.ListSalesChannelTypes]: ({}: EndpointInput<Endpoint.ListSalesChannelTypes>) => [
		Endpoint.ListSalesChannelTypes
	],
	[Endpoint.GetSalesChannelType]: ({ path }: EndpointInput<Endpoint.GetSalesChannelType>) => [
		Endpoint.GetSalesChannelType,
		createStableQueryKey(path)
	],
	[Endpoint.ListSalesChannelGroups]: ({}: EndpointInput<Endpoint.ListSalesChannelGroups>) => [
		Endpoint.ListSalesChannelGroups
	],
	[Endpoint.GetSalesChannelGroup]: ({ path }: EndpointInput<Endpoint.GetSalesChannelGroup>) => [
		Endpoint.GetSalesChannelGroup,
		createStableQueryKey(path)
	],
	[Endpoint.ListSalesChannels]: ({}: EndpointInput<Endpoint.ListSalesChannels>) => [Endpoint.ListSalesChannels],
	[Endpoint.GetSalesChannel]: ({ path }: EndpointInput<Endpoint.GetSalesChannel>) => [
		Endpoint.GetSalesChannel,
		createStableQueryKey(path)
	],
	[Endpoint.ListPointOfSaleGroups]: ({}: EndpointInput<Endpoint.ListPointOfSaleGroups>) => [
		Endpoint.ListPointOfSaleGroups
	],
	[Endpoint.GetPointOfSaleGroup]: ({ path }: EndpointInput<Endpoint.GetPointOfSaleGroup>) => [
		Endpoint.GetPointOfSaleGroup,
		createStableQueryKey(path)
	],
	[Endpoint.ListPointsOfSale]: ({}: EndpointInput<Endpoint.ListPointsOfSale>) => [Endpoint.ListPointsOfSale],
	[Endpoint.GetPointOfSaleHierarchy]: ({}: EndpointInput<Endpoint.GetPointOfSaleHierarchy>) => [
		Endpoint.GetPointOfSaleHierarchy
	],
	[Endpoint.GetCurrentPointOfSale]: ({}: EndpointInput<Endpoint.GetCurrentPointOfSale>) => [
		Endpoint.GetCurrentPointOfSale
	],
	[Endpoint.GetPointOfSale]: ({ path }: EndpointInput<Endpoint.GetPointOfSale>) => [
		Endpoint.GetPointOfSale,
		createStableQueryKey(path)
	],
	[Endpoint.GetPointOfSaleDeviceStatus]: ({ path }: EndpointInput<Endpoint.GetPointOfSaleDeviceStatus>) => [
		Endpoint.GetPointOfSaleDeviceStatus,
		createStableQueryKey(path)
	],
	[Endpoint.LookupDeviceEnrollmentByCode]: ({ path }: EndpointInput<Endpoint.LookupDeviceEnrollmentByCode>) => [
		Endpoint.LookupDeviceEnrollmentByCode,
		createStableQueryKey(path)
	],
	[Endpoint.GetGmsPlayTracks]: ({}: EndpointInput<Endpoint.GetGmsPlayTracks>) => [Endpoint.GetGmsPlayTracks],
	[Endpoint.ListPublishableKeys]: ({ query }: EndpointInput<Endpoint.ListPublishableKeys>) => [
		Endpoint.ListPublishableKeys,
		createStableQueryKey(query)
	],
	[Endpoint.GetPublishableKey]: ({ path }: EndpointInput<Endpoint.GetPublishableKey>) => [
		Endpoint.GetPublishableKey,
		createStableQueryKey(path)
	],
	[Endpoint.ListDevices]: ({}: EndpointInput<Endpoint.ListDevices>) => [Endpoint.ListDevices],
	[Endpoint.GetCurrentDevice]: ({}: EndpointInput<Endpoint.GetCurrentDevice>) => [Endpoint.GetCurrentDevice],
	[Endpoint.GetDevice]: ({ path }: EndpointInput<Endpoint.GetDevice>) => [
		Endpoint.GetDevice,
		createStableQueryKey(path)
	],
	[Endpoint.GetDeviceStatus]: ({ path }: EndpointInput<Endpoint.GetDeviceStatus>) => [
		Endpoint.GetDeviceStatus,
		createStableQueryKey(path)
	],
	[Endpoint.LookupDeviceEnrollmentByCodeForDevice]: ({
		path
	}: EndpointInput<Endpoint.LookupDeviceEnrollmentByCodeForDevice>) => [
		Endpoint.LookupDeviceEnrollmentByCodeForDevice,
		createStableQueryKey(path)
	],
	[Endpoint.ListProducts]: ({ query }: EndpointInput<Endpoint.ListProducts>) => [
		Endpoint.ListProducts,
		createStableQueryKey(query)
	],
	[Endpoint.ListProductGroupsForPointOfSale]: ({
		path,
		query
	}: EndpointInput<Endpoint.ListProductGroupsForPointOfSale>) => [
		Endpoint.ListProductGroupsForPointOfSale,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetProductMenu]: ({ path, query }: EndpointInput<Endpoint.GetProductMenu>) => [
		Endpoint.GetProductMenu,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.ListAvailableProductsForSalesChannel]: ({
		path,
		query
	}: EndpointInput<Endpoint.ListAvailableProductsForSalesChannel>) => [
		Endpoint.ListAvailableProductsForSalesChannel,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetProductFromBarcode]: ({ path }: EndpointInput<Endpoint.GetProductFromBarcode>) => [
		Endpoint.GetProductFromBarcode,
		createStableQueryKey(path)
	],
	[Endpoint.ListProductGroups]: ({ query }: EndpointInput<Endpoint.ListProductGroups>) => [
		Endpoint.ListProductGroups,
		createStableQueryKey(query)
	],
	[Endpoint.GetProductGroup]: ({ path }: EndpointInput<Endpoint.GetProductGroup>) => [
		Endpoint.GetProductGroup,
		createStableQueryKey(path)
	],
	[Endpoint.ListProductGroupFees]: ({ path }: EndpointInput<Endpoint.ListProductGroupFees>) => [
		Endpoint.ListProductGroupFees,
		createStableQueryKey(path)
	],
	[Endpoint.GetProductGroupFee]: ({ path }: EndpointInput<Endpoint.GetProductGroupFee>) => [
		Endpoint.GetProductGroupFee,
		createStableQueryKey(path)
	],
	[Endpoint.ListProductCategories]: ({ query }: EndpointInput<Endpoint.ListProductCategories>) => [
		Endpoint.ListProductCategories,
		createStableQueryKey(query)
	],
	[Endpoint.GetProductCategoryHierarchy]: ({}: EndpointInput<Endpoint.GetProductCategoryHierarchy>) => [
		Endpoint.GetProductCategoryHierarchy
	],
	[Endpoint.GetProductCategory]: ({ path }: EndpointInput<Endpoint.GetProductCategory>) => [
		Endpoint.GetProductCategory,
		createStableQueryKey(path)
	],
	[Endpoint.GetProduct]: ({ path }: EndpointInput<Endpoint.GetProduct>) => [
		Endpoint.GetProduct,
		createStableQueryKey(path)
	],
	[Endpoint.GetProductEdit]: ({ path }: EndpointInput<Endpoint.GetProductEdit>) => [
		Endpoint.GetProductEdit,
		createStableQueryKey(path)
	],
	[Endpoint.ListProductBarcodes]: ({ path }: EndpointInput<Endpoint.ListProductBarcodes>) => [
		Endpoint.ListProductBarcodes,
		createStableQueryKey(path)
	],
	[Endpoint.GetProductPricing]: ({ path }: EndpointInput<Endpoint.GetProductPricing>) => [
		Endpoint.GetProductPricing,
		createStableQueryKey(path)
	],
	[Endpoint.GetProductOrganization]: ({ path }: EndpointInput<Endpoint.GetProductOrganization>) => [
		Endpoint.GetProductOrganization,
		createStableQueryKey(path)
	],
	[Endpoint.ListProductImages]: ({ path }: EndpointInput<Endpoint.ListProductImages>) => [
		Endpoint.ListProductImages,
		createStableQueryKey(path)
	],
	[Endpoint.ListProductVariants]: ({ path }: EndpointInput<Endpoint.ListProductVariants>) => [
		Endpoint.ListProductVariants,
		createStableQueryKey(path)
	],
	[Endpoint.GetProductVariant]: ({ path }: EndpointInput<Endpoint.GetProductVariant>) => [
		Endpoint.GetProductVariant,
		createStableQueryKey(path)
	],
	[Endpoint.ListVariantBarcodes]: ({ path }: EndpointInput<Endpoint.ListVariantBarcodes>) => [
		Endpoint.ListVariantBarcodes,
		createStableQueryKey(path)
	],
	[Endpoint.GetVariantPricing]: ({ path }: EndpointInput<Endpoint.GetVariantPricing>) => [
		Endpoint.GetVariantPricing,
		createStableQueryKey(path)
	],
	[Endpoint.ListUnitsOfMeasure]: ({}: EndpointInput<Endpoint.ListUnitsOfMeasure>) => [Endpoint.ListUnitsOfMeasure],
	[Endpoint.ListAssets]: ({ query }: EndpointInput<Endpoint.ListAssets>) => [
		Endpoint.ListAssets,
		createStableQueryKey(query)
	],
	[Endpoint.ListAssetTags]: ({ query }: EndpointInput<Endpoint.ListAssetTags>) => [
		Endpoint.ListAssetTags,
		createStableQueryKey(query)
	],
	[Endpoint.GetAsset]: ({ path }: EndpointInput<Endpoint.GetAsset>) => [
		Endpoint.GetAsset,
		createStableQueryKey(path)
	],
	[Endpoint.ListMarketingChannels]: ({}: EndpointInput<Endpoint.ListMarketingChannels>) => [
		Endpoint.ListMarketingChannels
	],
	[Endpoint.GetMarketingChannel]: ({ path }: EndpointInput<Endpoint.GetMarketingChannel>) => [
		Endpoint.GetMarketingChannel,
		createStableQueryKey(path)
	],
	[Endpoint.GetFeedLayout]: ({ path, query }: EndpointInput<Endpoint.GetFeedLayout>) => [
		Endpoint.GetFeedLayout,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetChannelLayout]: ({ path, query }: EndpointInput<Endpoint.GetChannelLayout>) => [
		Endpoint.GetChannelLayout,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetChannelResolvedContent]: ({ path, query }: EndpointInput<Endpoint.GetChannelResolvedContent>) => [
		Endpoint.GetChannelResolvedContent,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.ListMarketingContent]: ({ query }: EndpointInput<Endpoint.ListMarketingContent>) => [
		Endpoint.ListMarketingContent,
		createStableQueryKey(query)
	],
	[Endpoint.GetMarketingContent]: ({ path }: EndpointInput<Endpoint.GetMarketingContent>) => [
		Endpoint.GetMarketingContent,
		createStableQueryKey(path)
	],
	[Endpoint.ListOrders]: ({ query }: EndpointInput<Endpoint.ListOrders>) => [
		Endpoint.ListOrders,
		createStableQueryKey(query)
	],
	[Endpoint.GetOrder]: ({ path }: EndpointInput<Endpoint.GetOrder>) => [
		Endpoint.GetOrder,
		createStableQueryKey(path)
	],
	[Endpoint.GetExitCode]: ({ path }: EndpointInput<Endpoint.GetExitCode>) => [
		Endpoint.GetExitCode,
		createStableQueryKey(path)
	],
	[Endpoint.GetOrderReceiptUrl]: ({ path }: EndpointInput<Endpoint.GetOrderReceiptUrl>) => [
		Endpoint.GetOrderReceiptUrl,
		createStableQueryKey(path)
	],
	[Endpoint.GetOrderTransaction]: ({ path }: EndpointInput<Endpoint.GetOrderTransaction>) => [
		Endpoint.GetOrderTransaction,
		createStableQueryKey(path)
	],
	[Endpoint.GetBagCheck]: ({ path }: EndpointInput<Endpoint.GetBagCheck>) => [
		Endpoint.GetBagCheck,
		createStableQueryKey(path)
	],
	[Endpoint.ListPromotions]: ({ query }: EndpointInput<Endpoint.ListPromotions>) => [
		Endpoint.ListPromotions,
		createStableQueryKey(query)
	],
	[Endpoint.GetPromotion]: ({ path }: EndpointInput<Endpoint.GetPromotion>) => [
		Endpoint.GetPromotion,
		createStableQueryKey(path)
	],
	[Endpoint.ListPromotionGroups]: ({ query }: EndpointInput<Endpoint.ListPromotionGroups>) => [
		Endpoint.ListPromotionGroups,
		createStableQueryKey(query)
	],
	[Endpoint.ListCountries]: ({}: EndpointInput<Endpoint.ListCountries>) => [Endpoint.ListCountries],
	[Endpoint.GetCountry]: ({ path }: EndpointInput<Endpoint.GetCountry>) => [
		Endpoint.GetCountry,
		createStableQueryKey(path)
	],
	[Endpoint.ListCountryTaxRates]: ({ path }: EndpointInput<Endpoint.ListCountryTaxRates>) => [
		Endpoint.ListCountryTaxRates,
		createStableQueryKey(path)
	],
	[Endpoint.ListTaxRates]: ({}: EndpointInput<Endpoint.ListTaxRates>) => [Endpoint.ListTaxRates],
	[Endpoint.GetAvailableExports]: ({}: EndpointInput<Endpoint.GetAvailableExports>) => [Endpoint.GetAvailableExports],
	[Endpoint.GetSAFTExportInfo]: ({}: EndpointInput<Endpoint.GetSAFTExportInfo>) => [Endpoint.GetSAFTExportInfo],
	[Endpoint.ListGiftCards]: ({ query }: EndpointInput<Endpoint.ListGiftCards>) => [
		Endpoint.ListGiftCards,
		createStableQueryKey(query)
	],
	[Endpoint.GetGiftCard]: ({ path }: EndpointInput<Endpoint.GetGiftCard>) => [
		Endpoint.GetGiftCard,
		createStableQueryKey(path)
	],
	[Endpoint.ListCompanies]: ({ query }: EndpointInput<Endpoint.ListCompanies>) => [
		Endpoint.ListCompanies,
		createStableQueryKey(query)
	],
	[Endpoint.GetCompany]: ({ path }: EndpointInput<Endpoint.GetCompany>) => [
		Endpoint.GetCompany,
		createStableQueryKey(path)
	],
	[Endpoint.ListCompanySettlements]: ({ path, query }: EndpointInput<Endpoint.ListCompanySettlements>) => [
		Endpoint.ListCompanySettlements,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.ListBusinessCards]: ({ query }: EndpointInput<Endpoint.ListBusinessCards>) => [
		Endpoint.ListBusinessCards,
		createStableQueryKey(query)
	],
	[Endpoint.GetBusinessCard]: ({ path }: EndpointInput<Endpoint.GetBusinessCard>) => [
		Endpoint.GetBusinessCard,
		createStableQueryKey(path)
	],
	[Endpoint.GetBusinessCardTransactions]: ({ path, query }: EndpointInput<Endpoint.GetBusinessCardTransactions>) => [
		Endpoint.GetBusinessCardTransactions,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetSettlement]: ({ path }: EndpointInput<Endpoint.GetSettlement>) => [
		Endpoint.GetSettlement,
		createStableQueryKey(path)
	],
	[Endpoint.GetAnalytics]: ({ query }: EndpointInput<Endpoint.GetAnalytics>) => [
		Endpoint.GetAnalytics,
		createStableQueryKey(query)
	],
	[Endpoint.GetBasketAnalysis]: ({ query }: EndpointInput<Endpoint.GetBasketAnalysis>) => [
		Endpoint.GetBasketAnalysis,
		createStableQueryKey(query)
	],
	[Endpoint.GetUserAnalytics]: ({ query }: EndpointInput<Endpoint.GetUserAnalytics>) => [
		Endpoint.GetUserAnalytics,
		createStableQueryKey(query)
	],
	[Endpoint.GetRevenueReport]: ({ query }: EndpointInput<Endpoint.GetRevenueReport>) => [
		Endpoint.GetRevenueReport,
		createStableQueryKey(query)
	],
	[Endpoint.GetProductReport]: ({ query }: EndpointInput<Endpoint.GetProductReport>) => [
		Endpoint.GetProductReport,
		createStableQueryKey(query)
	],
	[Endpoint.ListCustomers]: ({ query }: EndpointInput<Endpoint.ListCustomers>) => [
		Endpoint.ListCustomers,
		createStableQueryKey(query)
	],
	[Endpoint.GetCustomer]: ({ path }: EndpointInput<Endpoint.GetCustomer>) => [
		Endpoint.GetCustomer,
		createStableQueryKey(path)
	],
	[Endpoint.ListCustomerGroups]: ({ query }: EndpointInput<Endpoint.ListCustomerGroups>) => [
		Endpoint.ListCustomerGroups,
		createStableQueryKey(query)
	],
	[Endpoint.GetCustomerGroup]: ({ path }: EndpointInput<Endpoint.GetCustomerGroup>) => [
		Endpoint.GetCustomerGroup,
		createStableQueryKey(path)
	],
	[Endpoint.ListLoyaltyCards]: ({ query }: EndpointInput<Endpoint.ListLoyaltyCards>) => [
		Endpoint.ListLoyaltyCards,
		createStableQueryKey(query)
	],
	[Endpoint.GetLoyaltyCard]: ({ path }: EndpointInput<Endpoint.GetLoyaltyCard>) => [
		Endpoint.GetLoyaltyCard,
		createStableQueryKey(path)
	],
	[Endpoint.GetLoyaltyCardRedemptions]: ({ path, query }: EndpointInput<Endpoint.GetLoyaltyCardRedemptions>) => [
		Endpoint.GetLoyaltyCardRedemptions,
		createStableQueryKey(path),
		createStableQueryKey(query)
	],
	[Endpoint.GetDashboardFeatures]: ({}: EndpointInput<Endpoint.GetDashboardFeatures>) => [
		Endpoint.GetDashboardFeatures
	],
	[Endpoint.ListInventoryLocations]: ({ query }: EndpointInput<Endpoint.ListInventoryLocations>) => [
		Endpoint.ListInventoryLocations,
		createStableQueryKey(query)
	],
	[Endpoint.SearchInventoryVariants]: ({ query }: EndpointInput<Endpoint.SearchInventoryVariants>) => [
		Endpoint.SearchInventoryVariants,
		createStableQueryKey(query)
	],
	[Endpoint.GetInventoryLocation]: ({ path }: EndpointInput<Endpoint.GetInventoryLocation>) => [
		Endpoint.GetInventoryLocation,
		createStableQueryKey(path)
	],
	[Endpoint.GetInventorySettings]: ({}: EndpointInput<Endpoint.GetInventorySettings>) => [
		Endpoint.GetInventorySettings
	],
	[Endpoint.ListInventoryLevels]: ({ query }: EndpointInput<Endpoint.ListInventoryLevels>) => [
		Endpoint.ListInventoryLevels,
		createStableQueryKey(query)
	],
	[Endpoint.GetInventorySummary]: ({ query }: EndpointInput<Endpoint.GetInventorySummary>) => [
		Endpoint.GetInventorySummary,
		createStableQueryKey(query)
	],
	[Endpoint.GetInventoryLevel]: ({ path }: EndpointInput<Endpoint.GetInventoryLevel>) => [
		Endpoint.GetInventoryLevel,
		createStableQueryKey(path)
	],
	[Endpoint.ListStockMovements]: ({ query }: EndpointInput<Endpoint.ListStockMovements>) => [
		Endpoint.ListStockMovements,
		createStableQueryKey(query)
	],
	[Endpoint.ListSuppliers]: ({ query }: EndpointInput<Endpoint.ListSuppliers>) => [
		Endpoint.ListSuppliers,
		createStableQueryKey(query)
	],
	[Endpoint.GetSupplier]: ({ path }: EndpointInput<Endpoint.GetSupplier>) => [
		Endpoint.GetSupplier,
		createStableQueryKey(path)
	],
	[Endpoint.ListPurchaseOrders]: ({ query }: EndpointInput<Endpoint.ListPurchaseOrders>) => [
		Endpoint.ListPurchaseOrders,
		createStableQueryKey(query)
	],
	[Endpoint.GetPurchaseOrder]: ({ path }: EndpointInput<Endpoint.GetPurchaseOrder>) => [
		Endpoint.GetPurchaseOrder,
		createStableQueryKey(path)
	],
	[Endpoint.ListInventoryTransfers]: ({ query }: EndpointInput<Endpoint.ListInventoryTransfers>) => [
		Endpoint.ListInventoryTransfers,
		createStableQueryKey(query)
	],
	[Endpoint.GetInventoryTransfer]: ({ path }: EndpointInput<Endpoint.GetInventoryTransfer>) => [
		Endpoint.GetInventoryTransfer,
		createStableQueryKey(path)
	],
	[Endpoint.GetInventoryReportSummary]: ({ query }: EndpointInput<Endpoint.GetInventoryReportSummary>) => [
		Endpoint.GetInventoryReportSummary,
		createStableQueryKey(query)
	],
	[Endpoint.ListVariantSuppliers]: ({ query }: EndpointInput<Endpoint.ListVariantSuppliers>) => [
		Endpoint.ListVariantSuppliers,
		createStableQueryKey(query)
	],
	[Endpoint.ListIntegrations]: ({}: EndpointInput<Endpoint.ListIntegrations>) => [Endpoint.ListIntegrations],
	[Endpoint.GetTeyaIntegration]: ({}: EndpointInput<Endpoint.GetTeyaIntegration>) => [Endpoint.GetTeyaIntegration],
	[Endpoint.ListTeyaStores]: ({}: EndpointInput<Endpoint.ListTeyaStores>) => [Endpoint.ListTeyaStores],
	[Endpoint.ListTeyaTerminals]: ({ path }: EndpointInput<Endpoint.ListTeyaTerminals>) => [
		Endpoint.ListTeyaTerminals,
		createStableQueryKey(path)
	],
	[Endpoint.PollRapydDeviceToken]: ({ path }: EndpointInput<Endpoint.PollRapydDeviceToken>) => [
		Endpoint.PollRapydDeviceToken,
		createStableQueryKey(path)
	],
	[Endpoint.GetIntegrationConfig]: ({ path }: EndpointInput<Endpoint.GetIntegrationConfig>) => [
		Endpoint.GetIntegrationConfig,
		createStableQueryKey(path)
	],
	[Endpoint.GetBillingPlan]: ({}: EndpointInput<Endpoint.GetBillingPlan>) => [Endpoint.GetBillingPlan],
	[Endpoint.GetAppUpdateBranches]: ({}: EndpointInput<Endpoint.GetAppUpdateBranches>) => [
		Endpoint.GetAppUpdateBranches
	],
	[Endpoint.GetAppUpdates]: ({ path, query }: EndpointInput<Endpoint.GetAppUpdates>) => [
		Endpoint.GetAppUpdates,
		createStableQueryKey(path),
		createStableQueryKey(query)
	]
}
